// The delta-v map's Go as a state machine: one TransferPlan plus the ship's
// state each frame in, an attitude, throttle and warp command out. Pure.

// The plan is heliocentric in KILOMETRES and impulsive; this module is metres
// and heliocentric, and the caller composes and applies the parent-relative part.

import { flightNose, stepFlight } from './flight.ts';
import type {
  FlightAxes, FlightConfig, FlightField, FlightPull, FlightQuat, FlightState, FlightVec,
} from './flight.ts';
import {
  DEFAULT_PARKING_ALTITUDE_KM, createFiniteCaptureMemo, finiteCaptureOf, finiteEscapeEnd, lambert, warmFiniteCapture,
} from './transfer.ts';
import type {
  FiniteCapture, FiniteCaptureMemo, LocalBurnPoint, TransferBurn, TransferPlan,
} from './transfer.ts';

const SECONDS_PER_DAY = 86400;

/** Which leg of the trip the autopilot is on. */
export type AutopilotPhase =
  | 'wait'
  | 'depart'
  | 'coast'
  | 'trim'
  | 'capture'
  | 'arrived'
  | 'handback'
  | 'refused';

/** What `nextEventS` is counting down to, where it is a dated step of the plan
 *  rather than the end of a burn in hand. */
export type AutopilotStep = 'window' | 'course-fix' | 'arrival';

/** Why the autopilot let go of the stick. */
export interface AutopilotEnd {
  readonly phase: 'arrived' | 'handback' | 'refused';
  /** One line for the panel, in the player's own terms. */
  readonly reason: string;
}

/** Everything the guidance needs to look up, and nothing it may look up twice. */
export interface AutopilotEnv {
  /** A body's heliocentric place at an epoch, metres, into `out`. */
  bodyAt(id: string, jd: number, out: FlightVec): void;
  /** The same body's heliocentric velocity, metres a second, into `out`. */
  bodyVelocityAt(id: string, jd: number, out: FlightVec): void;
  /** A body's GM, m3/s2. 0 where the registry carries no mass for it. */
  gmOf(id: string): number;
  /** A body's mean radius, metres. 0 where the registry carries none. What the
   *  arrival is sized against: a moon is four orders of magnitude smaller than
   *  the planet the same aim was set for. */
  radiusOf(id: string): number;
}

export interface AutopilotConfig {
  /** Metres a second squared at full throttle: `flightConfig.accel`. */
  readonly accelMps2: number;
  /** Radians a real second the nose pivots: `flightConfig.turnRate`. */
  readonly turnRateRadS: number;
  /** The warp ladder's own rates, sim seconds a real second, ascending. */
  readonly warpRates: readonly number[];
  /** Frames a real second the warp rules price a frame at. */
  readonly framesPerSecond?: number;
  /** Bodies the guidance's own propagation pulls with. The plan's two ends and
   *  the Sun unless the caller names more. */
  readonly modelIds?: readonly string[];
  /** How far from the destination's centre the arrival is aimed, metres.
   *  See arrivalStandoffFor. */
  readonly arrivalStandoffM?: number;
  /** The parking altitude the map this plan came off priced its capture into,
   *  metres. Absent takes the plan's own `captureAltitudeKm`, else
   *  `src/sim/transfer.ts`'s default; a caller planning at another altitude
   *  passes it here so the aim moves with the price. */
  readonly parkingAltitudeM?: number;
  /** Ceiling on one trim, metres a second. A solve asking for more takes this
   *  much along the direction it asked for and the next trim carries on. */
  readonly trimCeilingMps?: number;
  /** Ceiling on the whole trip's trims, metres a second, past which the
   *  autopilot refuses rather than spending the tank on a plan going wrong. */
  readonly trimBudgetMps?: number;
  /** Steering dropped: the throttle goes along whatever nose the ship carries.
   *  `tools/preview/transit-check.mjs --inject no-steer` sets it and nothing a
   *  player reaches does. */
  readonly noSteer?: boolean;
  /** Departure stopped on ENERGY rather than on the plan's own conic and opened
   *  untimed, which leaves the asymptote degrees off. The transit check's own
   *  control sets it. */
  readonly progradeDeparture?: boolean;
  /** The departure opened where the schedule centres the instant push rather
   *  than timed off the ship's orbit: GO as it flew before.
   *  `tools/preview/transit-check.mjs --inject untimed-departure` sets it and
   *  nothing a player reaches does. */
  readonly untimedDeparture?: boolean;
  /** The bound departure law dropped: a transfer that stays inside the parent's
   *  well is departed on the escape law anyway, which burns the ship out of the
   *  well the plan meant it to stay in. `tools/preview/transit-check.mjs
   *  --inject escape-departure` sets it and nothing a player reaches does. */
  readonly escapeDeparture?: boolean;
  /** A burn asks for the rate ONE guidance step a frame buys, the way it did
   *  before the caller learned to split a frame into `burnStepS` steps: a six
   *  hour burn then costs six real minutes. `--inject burn-holds-rate` sets it
   *  and nothing a player reaches does. */
  readonly burnHoldsRate?: boolean;
  /** A slewing frame of a burn hands back the unsplit rate and no step, the way
   *  it did before the ceiling and the step learned to ride the slew too: the
   *  frame after it flies its whole span along the nose that frame froze on.
   *  `tools/preview/descent-dojo.mjs --transit --inject burn-frozen-nose` sets
   *  it and nothing a player reaches does. */
  readonly burnFrozenNose?: boolean;
  /** A turn to a burn's attitude flown the way it was before it was spread over
   *  rendered frames: on a split frame's simulated seconds, with the clock free
   *  to rise under it, so the turn lands in one frame.
   *  `tools/preview/burn-slew-check.mjs --inject one-frame-turn` sets it and
   *  nothing a player reaches does. */
  readonly slewInOneFrame?: boolean;
  /** The arrival and the timing sized for a trip of months across astronomical
   *  units whatever trip this is: the fixed standoff, the absolute last trim
   *  rungs and a nose turn the warp may spend the flight on.
   *  `tools/preview/transit-check.mjs --inject arrival-sized-for-au` sets it and
   *  nothing a player reaches does. */
  readonly arrivalSizedForAu?: boolean;
  /** The arrival aimed the standoff short of the destination's CENTRE again,
   *  so a planet is targeted under its own surface. `tools/preview/
   *  transit-check.mjs --inject aim-through-centre` sets it and nothing a
   *  player reaches does. */
  readonly aimThroughCentre?: boolean;
  /** A nose turn charged against the whole trip again rather than against what
   *  is left of it, so a slew at an hour a second spends half the last hour.
   *  `tools/preview/transit-check.mjs --inject slew-on-trip-budget` sets it and
   *  nothing a player reaches does. */
  readonly slewOnTripBudget?: boolean;
  /** The pre-epoch alignment lead capped at a hundredth of the time still to
   *  run to the arrival epoch again - a cap nothing before that epoch can
   *  satisfy, so the capture opens on the first frame PAST its own epoch with
   *  the nose still where the coast left it. `tools/preview/transit-check.mjs
   *  --inject capture-opens-at-epoch` sets it and nothing a player reaches does. */
  readonly captureOpensAtEpoch?: boolean;
  /** An arrival into orbit aimed the standoff short of the destination along
   *  the approach again, a point only a fall straight at the centre reaches.
   *  `tools/preview/transit-check.mjs --inject aim-on-approach-line` sets it
   *  and nothing a player reaches does. */
  readonly aimOnApproachLine?: boolean;
  /** A bound departure flown on the ellipse's energy along the orbit the ship
   *  is already on again, so a turn into the moon's plane the plan prices at
   *  the departure, and any push the plan points off the ship's motion, is left
   *  to the course fixes. `tools/preview/transit-check.mjs --inject
   *  departure-in-own-plane` sets it and nothing a player reaches does. */
  readonly departureInOwnPlane?: boolean;
  /** The capture flown the way it was before it was steered: aimed at the
   *  parking radius, opened at the arrival epoch, held along the relative
   *  velocity the coast left and stopped once the plan's priced push is spent.
   *  `tools/preview/transit-check.mjs --inject capture-on-priced-push` and
   *  `?capturepricedpush=1` set it and nothing a player reaches does. */
  readonly capturePricedPush?: boolean;
  /** A capture into orbit held along the retrograde to the parking orbit's
   *  energy again, with no tilt steering it onto the circle, so the orbit it
   *  hands back is as round as the brake's start happened to leave it.
   *  `tools/preview/transit-check.mjs --inject capture-unguided` sets it and
   *  nothing a player reaches does. */
  readonly captureUnguided?: boolean;
  /** A velocity match onto a moon's orbit flown along the moon's own direction
   *  of motion rather than turned onto the ship's own tangent.
   *  `?coorbitunrotated=1` sets it and nothing a player reaches does. */
  readonly coOrbitUnrotated?: boolean;
  /** The park beside a moon put back on the moon's own track and orbital
   *  energy, whose period is longer than the moon's as its ephemeris flies it,
   *  so a ship parked ahead of the moon falls back onto it.
   *  `tools/preview/moon-arrival-coast.mjs --inject park-on-moon-energy` and
   *  `?parkonmoonenergy=1` set it and nothing a player reaches does. */
  readonly parkOnMoonEnergy?: boolean;
  /** A velocity match aimed at the park alone again, on a line through the moon
   *  that only the brake stops the ship short on. `tools/preview/
   *  transit-check.mjs --inject aim-through-moon` sets it and nothing a player
   *  reaches does. */
  readonly aimThroughMoon?: boolean;
  /** Integrator steps ONE `update()` may spend on a guidance solve before it
   *  hands the frame back. 0 or less runs a solve whole inside the call that
   *  opens it. See SOLVE_STEP_BUDGET. */
  readonly solveStepBudget?: number;
  /** Milliseconds one `update()` may spend on a solve, checked inside the step
   *  loop. The step count is the upper bound; this is what holds the call
   *  under the frame budget on a machine slower than the one it was set on. */
  readonly solveMsBudget?: number;
  /** The old frame budget: a solve's slice gets its own milliseconds on top of
   *  whatever the call spent first, and every re-pricing of a finite capture
   *  flies its whole climb again. `tools/preview/transit-check.mjs --inject
   *  slice-own-budget` sets it and nothing a player reaches does. */
  readonly sliceOwnBudget?: boolean;
}

export interface AutopilotInput {
  /** TDB Julian date at the START of this frame. */
  readonly jd: number;
  /** Simulated seconds this frame is about to fly. */
  readonly dtSimS: number;
  /** Seconds this call's attitude turn gets: real ones on a whole frame, the
   *  step's simulated ones on a burn step. */
  readonly attitudeDtS: number;
  /** Real seconds of the rendered frame this call is a share of. A burn's turn
   *  wider than `SLEW_FRAME_RAD` is flown on these rather than on `attitudeDtS`,
   *  so it spans rendered frames at any clock. Absent, `attitudeDtS`. */
  readonly slewRealS?: number;
  /** A rendered frame's own update, which may spend a slice on the next course
   *  fix's solve ahead of its date. Absent, the solve opens at the date and the
   *  clock stands still while it runs. */
  readonly solveAhead?: boolean;
  /** The flight state, read only: the attitude is what the steering closes on. */
  readonly state: FlightState;
  /** The ship's heliocentric place, metres. */
  readonly at: FlightVec;
  /** The ship's heliocentric velocity, metres a second. */
  readonly velocity: FlightVec;
  /** A pilot control was touched this frame. The stick is theirs again. */
  readonly pilotInput: boolean;
  /** The most of the drive this frame will deliver, [0, 1]: the heat cap on a
   *  hull whose radiators cannot shed what a full burn makes. 1 where absent. */
  readonly throttleCap?: number;
  /** Share of full thrust a burn opened from here holds once it has heated the
   *  hull (`sustainedThrottle` in src/scene/heat.ts), which an idle ship's
   *  `throttleCap` cannot show yet. What the departure is timed at; 1 where absent. */
  readonly burnThrustShare?: number;
}

export interface AutopilotCommand {
  readonly phase: AutopilotPhase;
  /** Held axes for `stepFlight`, each [-1, 1]. */
  readonly axes: FlightAxes;
  /** [-1, 1] along the nose. Feathered on a burn's last frame. */
  readonly throttle: number;
  /** Sim seconds a real second the autopilot asks the clock for. The caller's
   *  own gates - an engagement, a body close by - still get to say no. */
  readonly warpRate: number;
  /** The FASTEST the clock may run at this point of the trip, which is what a
   *  rate the player asks for mid-flight is held to. The same as `warpRate`
   *  everywhere but an open engine, where the autopilot asks for the rate that
   *  keeps the burn something to watch and the player may have the rate the
   *  guidance can still steer at. */
  readonly warpCeiling: number;
  /** Where the nose is wanted, unit, in the flight frame. Null while the
   *  autopilot does not care where it points. */
  readonly noseTarget: FlightVec | null;
  /** Radians the nose is off that target, 0 where there is none. */
  readonly noseErrorRad: number;
  /** Metres a second still owed on the burn in hand; 0 outside one. */
  readonly dvToGoMps: number;
  /** Simulated seconds to the next thing that happens, for the panel's count.
   *  Inside a burn that is the burn's own end: the engine-on seconds left on it
   *  at the throttle this command carries. */
  readonly nextEventS: number;
  /** Which dated step of the plan `nextEventS` counts to, or null where it
   *  counts to the end of a burn in hand and no clock skip can be offered. */
  readonly nextStep: AutopilotStep | null;
  /** Simulated seconds ONE guidance step of the burn in hand may cover. A
   *  caller flying a frame longer than this splits it into that many steps, or
   *  the throttle goes along one frozen nose for the whole frame. 0 where there
   *  is nothing to split: outside a burn, and while one waits for its start. */
  readonly burnStepS: number;
  /** Metres a second the trims have spent so far. */
  readonly trimSpentMps: number;
  /** Metres a second every burn of the trip has spent so far, the departure and
   *  the capture included, against the plan's own `totalDvKmS`. */
  readonly spentMps: number;
  /** A guidance solve is part way through and this is the last command held
   *  while it finishes. `freezes` marks those frames for the caller. */
  readonly solving?: boolean;
  /** The next course fix is already solved, ahead of its date. */
  readonly fixSolved?: boolean;
  /** In a burn, the fastest clock its guidance steps still carry, off the ladder:
   *  `warpCeiling` is the rung under it. */
  readonly burnSpanRate?: number;
  /** A wait whose departure is still to be timed off the ship's own orbit, so
   *  where the ship is round that orbit decides nothing yet. */
  readonly departureUntimed?: boolean;
  /** Sim seconds before `nextEventS` the step's nose turn opens: the departure's
   *  align lead while waiting, 0 where the count already runs to the turn. */
  readonly turnLeadS?: number;
  /** Sim seconds before the start within which an untimed escape is timed: the
   *  ship's orbital period, at least DEPARTURE_TIMING_REACH_S. Absent otherwise. */
  readonly timingReachS?: number;
  readonly done: boolean;
  readonly ended: AutopilotEnd | null;
}

export interface Autopilot {
  update(input: AutopilotInput): AutopilotCommand;
  /** Hand the stick back with no input to blame: a cancel button, a scene tear
   *  down. Idempotent. */
  cancel(reason?: string): void;
  /** True where the `update()` at this epoch runs a guidance solve. The caller
   *  must fly such a frame at zero simulated seconds and leave its clock alone,
   *  or the answer lands on a ship that has moved since the solve was seeded. */
  freezes(jd: number): boolean;
  /** TDB Julian date the capture's throttle is due to open: the planned epoch
   *  until the capture is in hand, then the one it was timed to off the
   *  approach. NaN on a plan with no capture. */
  captureDueJd(): number;
  readonly plan: TransferPlan;
  /** Metres the last course fix solved ahead of its date had the ship off where
   *  it was at the date; NaN before one. */
  readonly aheadOffM: number;
}

/**
 * Whether the guidance has a departure law for this plan, which is what decides
 * whether Go is offered for it.
 *
 * Two laws: a transfer that leaves the body's well is flown off the escape
 * conic, and one that stays inside it off the ellipse the plan carries on its
 * departure burn. A plan planned in a second plane - a moon of the body the
 * ship is at - is only the second kind, so its departure has to name that
 * ellipse; a tilted plan that does not is neither law and nothing here flies it.
 */
export function canAutopilotFly(plan: TransferPlan): boolean {
  const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
  if (departure === null) return false;
  if (plan.planeTiltDeg === null) return true;
  return (departure.boundSemiMajorKm ?? 0) > 0;
}

// How far above the destination's own surface the arrival is aimed: the parking
// altitude `src/sim/transfer.ts` prices the capture into, read off that module
// rather than written again here, so a map planned at another altitude moves
// the aim and the price together.
export const ARRIVAL_STANDOFF_M = DEFAULT_PARKING_ALTITUDE_KM * 1000;

/** The same aim at a body too small for it, in the destination's own radii:
 *  400 km is a tenth of Mars and 36 Phoboses. */
const ARRIVAL_STANDOFF_RADII = 10;

/** The altitude, metres, the plan's own capture is priced into: the arrival orbit it was planned for. */
export function plannedParkingAltitudeM(plan: TransferPlan): number {
  return plan.captureAltitudeKm !== undefined ? plan.captureAltitudeKm * 1000 : ARRIVAL_STANDOFF_M;
}

/**
 * Metres from the centre an arrival at a body of this radius is aimed: the
 * surface plus the parking altitude, which is the orbit the plan's own capture
 * is priced into. An aim that ends up under the surface is caught by the
 * transit check's lowest-over-the-surface capture reading, not by the solve
 * failing to converge.
 *
 * The aim and the price are the same orbit down to a radius of a tenth of the
 * parking altitude - 40 km at the default 400 - and under that they are not.
 * `transfer.ts` prices every capture at the surface plus the altitude, and this
 * caps the aim at ten radii, so at Phobos the ship is aimed at 122 km from the
 * centre and the capture was priced for 411. The cap stays: 400 km is 36 Phobos
 * radii, an arrival that stops that far short of an 11 km moon is a miss a
 * player can see, and `--inject arrival-sized-for-au` is what reads it. What a
 * capture costs from the aim rather than from the priced orbit is a pricing
 * question, and `src/sim/transfer.ts` owns it.
 */
export function arrivalStandoffFor(
  destinationRadiusM: number, parkingAltitudeM = ARRIVAL_STANDOFF_M,
): number {
  if (!(destinationRadiusM > 0)) return parkingAltitudeM;
  return destinationRadiusM + centreStandoffFor(destinationRadiusM, parkingAltitudeM);
}

/**
 * Where an arrival into orbit is aimed, from the destination's centre: the
 * periapsis at `periapsisM` of a hyperbola with the approach's own incoming
 * asymptote and excess speed, off the osculating conic of `rel` and `relVel`.
 * Off a hyperbola the velocity stands in for the asymptote and
 * `fallbackVInfMps` for the excess speed.
 */
export function periapsisAimFor(
  rel: FlightVec, relVel: FlightVec, gm: number, periapsisM: number,
  fallbackVInfMps: number, out: FlightVec,
): boolean {
  const r = norm(rel);
  const v = norm(relVel);
  if (!(r > 0) || !(v > 0) || !(gm > 0) || !(periapsisM > 0)) return false;
  let hx = rel.y * relVel.z - rel.z * relVel.y;
  let hy = rel.z * relVel.x - rel.x * relVel.z;
  let hz = rel.x * relVel.y - rel.y * relVel.x;
  const hl = Math.hypot(hx, hy, hz);
  const energy = (v * v) / 2 - gm / r;
  let sx = relVel.x / v;
  let sy = relVel.y / v;
  let sz = relVel.z / v;
  let vInf = fallbackVInfMps > 0 ? fallbackVInfMps : v;
  const radial = !(hl > 1e-9 * r * v);
  if (!radial) {
    hx /= hl;
    hy /= hl;
    hz /= hl;
    // Eccentricity vector, (v x h)/gm - r/|r|, with h taken at its full length.
    const ex = (relVel.y * hz - relVel.z * hy) * (hl / gm) - rel.x / r;
    const ey = (relVel.z * hx - relVel.x * hz) * (hl / gm) - rel.y / r;
    const ez = (relVel.x * hy - relVel.y * hx) * (hl / gm) - rel.z / r;
    const e = Math.hypot(ex, ey, ez);
    if (energy > 0 && e > 1) {
      // The velocity at the incoming asymptote, (e_hat + sqrt(e^2 - 1) q_hat) / e.
      const px = ex / e;
      const py = ey / e;
      const pz = ez / e;
      const k = Math.sqrt(e * e - 1);
      sx = (px + k * (hy * pz - hz * py)) / e;
      sy = (py + k * (hz * px - hx * pz)) / e;
      sz = (pz + k * (hx * py - hy * px)) / e;
      vInf = Math.sqrt(2 * energy);
    }
  } else {
    // No plane to swing in: any one through the asymptote costs the same.
    hx = sy;
    hy = -sx;
    hz = 0;
    let l = Math.hypot(hx, hy, hz);
    if (!(l > 1e-9)) {
      hx = 0;
      hy = sz;
      hz = -sy;
      l = Math.hypot(hx, hy, hz);
    }
    hx /= l;
    hy /= l;
    hz /= l;
  }
  // The side the asymptote passes the centre on, s x h, and the periapsis
  // swung round from the incoming direction by the asymptote's true anomaly.
  const bx = sy * hz - sz * hy;
  const by = sz * hx - sx * hz;
  const bz = sx * hy - sy * hx;
  const ePlanned = 1 + (periapsisM * vInf * vInf) / gm;
  const cosNu = -1 / ePlanned;
  const sinNu = Math.sqrt(1 - cosNu * cosNu);
  out.x = periapsisM * (-cosNu * sx + sinNu * bx);
  out.y = periapsisM * (-cosNu * sy + sinNu * by);
  out.z = periapsisM * (-cosNu * sz + sinNu * bz);
  return true;
}

/** The periapsis an arrival into orbit is aimed at, metres from the centre: the
 *  one a finite capture is priced from, and the standoff where the plan prices
 *  the capture as an instant push. Given an `approach` inside the capture's
 *  sphere, the finite capture is re-priced at that approach's own excess
 *  speed, which is the one the brake is timed against. `plannedM` stands in
 *  for the plan's own periapsis outside the sphere (`finiteAimAtDriveM`). */
export function captureAimPeriapsisM(
  capture: TransferBurn | null, standoffM: number,
  approach?: {
    readonly rel: FlightVec; readonly relVel: FlightVec; readonly gm: number; readonly accelMps2: number;
  },
  plannedM?: number,
  memo?: FiniteCaptureMemo,
): number {
  const finite = capture?.finiteCapture;
  if (finite === undefined) return standoffM;
  const planned = plannedM ?? finite.periapsisKm * 1000;
  if (approach === undefined || finite.sphereKm === null) return planned;
  const r = norm(approach.rel);
  const v = norm(approach.relVel);
  const energy = (v * v) / 2 - approach.gm / r;
  if (!(r < finite.sphereKm * 1000) || !(energy > 0) || !(approach.accelMps2 > 0)) return planned;
  const live = finiteCaptureOf(approach.gm / 1e9, finite.parkingRadiusKm,
    approach.accelMps2 / 1000, Math.sqrt(2 * energy) / 1000, memo);
  return live === null ? planned : live.capture.periapsisKm * 1000;
}

/** The periapsis a finite capture flown on `driveMps2` rather than the thrust
 *  the plan was priced with opens from, metres: a weaker drive brakes longer
 *  and has to start higher to end on the same orbit. Null where the plan
 *  carries no finite capture or the drive prices none. */
export function finiteAimAtDriveM(capture: TransferBurn | null, driveMps2: number): number | null {
  const finite = capture?.finiteCapture;
  if (capture === null || finite === undefined || finite.gmKm3S2 === undefined) return null;
  const priced = finiteCaptureOf(finite.gmKm3S2, finite.parkingRadiusKm, driveMps2 / 1000, capture.vInfKmS);
  return priced === null ? null : priced.capture.periapsisKm * 1000;
}

/** The closest the conic `rel` and `relVel` put the ship on about a body of
 *  `gm` comes to its centre, metres, bound or not. */
export function conicPeriapsisM(rel: FlightVec, relVel: FlightVec, gm: number): number {
  const r = norm(rel);
  const v = norm(relVel);
  const energy = (v * v) / 2 - gm / r;
  const h2 = (rel.y * relVel.z - rel.z * relVel.y) ** 2
    + (rel.z * relVel.x - rel.x * relVel.z) ** 2
    + (rel.x * relVel.y - rel.y * relVel.x) ** 2;
  const e = Math.sqrt(Math.max(0, 1 + (2 * energy * h2) / (gm * gm)));
  return h2 / (gm * (1 + e));
}

/** Seconds until the ship passes the periapsis of the hyperbola `rel` and
 *  `relVel` put it on about a body of `gm`, negative once past it, which is
 *  what a capture's brake is timed back from. NaN where that is no hyperbola. */
export function periapsisInS(rel: FlightVec, relVel: FlightVec, gm: number): number {
  const r = norm(rel);
  const v = norm(relVel);
  const energy = (v * v) / 2 - gm / r;
  if (!(gm > 0) || !(r > 0) || !(energy > 0)) return Number.NaN;
  const hl = Math.hypot(
    rel.y * relVel.z - rel.z * relVel.y,
    rel.z * relVel.x - rel.x * relVel.z,
    rel.x * relVel.y - rel.y * relVel.x);
  const e = Math.sqrt(1 + (2 * energy * hl * hl) / (gm * gm));
  if (!(e > 1)) return Number.NaN;
  const a = gm / (2 * energy);
  const f = Math.acosh(Math.max(1, (1 + r / a) / e));
  const fromPeriapsisS = Math.sqrt((a * a * a) / gm) * (e * Math.sinh(f) - f);
  return rel.x * relVel.x + rel.y * relVel.y + rel.z * relVel.z < 0
    ? fromPeriapsisS : -fromPeriapsisS;
}

/** What a velocity match joins the orbit of: the planet a moon trip stays in
 *  the well of, whatever its own conic's sign, the Sun for anything reached
 *  across open space. A moon leg is what sets `boundSemiMajorKm` on its
 *  departure at all, not its sign - a leg priced on a hyperbolic conic still
 *  owes its parent, not the Sun. */
export function matchParentIdFor(departure: TransferBurn | null, originId: string): string {
  return departure !== null && departure.boundSemiMajorKm !== null ? originId : 'sol';
}

/**
 * The velocity, relative to the parent, that puts a ship at `ship` on the
 * moon's own orbit turned about that orbit's pole onto the ship: the moon's
 * radial and along-track shares of its velocity laid on the ship's own radial
 * and on its own local tangent in the moon's orbital plane, at the speed the
 * moon's orbit has at the ship's distance. The same energy keeps the moon's
 * period and the same flight-path angle its shape, so a ship off the moon's
 * position or plane rides beside it. `unrotated` keeps the moon's own
 * direction of motion instead. `semiMajorM` sizes the orbit instead of the
 * moon's own energy. False where there is no such orbit.
 */
export function coOrbitVelocityFor(
  gm: number, moonAt: FlightVec, moonVel: FlightVec, ship: FlightVec, unrotated: boolean,
  out: FlightVec, semiMajorM?: number,
): boolean {
  const rm = norm(moonAt);
  const vm = norm(moonVel);
  const rs = norm(ship);
  const inverseA = semiMajorM !== undefined && semiMajorM > 0
    ? 1 / semiMajorM
    : 2 / rm - (vm * vm) / gm;
  const want = gm * (2 / rs - inverseA);
  if (!(gm > 0) || !(rm > 0) || !(vm > 0) || !(rs > 0) || !(want > 0)) return false;
  const speed = Math.sqrt(want);
  if (unrotated) {
    out.x = (moonVel.x / vm) * speed;
    out.y = (moonVel.y / vm) * speed;
    out.z = (moonVel.z / vm) * speed;
    return true;
  }
  const hx = moonAt.y * moonVel.z - moonAt.z * moonVel.y;
  const hy = moonAt.z * moonVel.x - moonAt.x * moonVel.z;
  const hz = moonAt.x * moonVel.y - moonAt.y * moonVel.x;
  const hl = Math.hypot(hx, hy, hz);
  if (!(hl > 0)) return false;
  const radial = (moonAt.x * moonVel.x + moonAt.y * moonVel.y + moonAt.z * moonVel.z) / (rm * vm);
  const along = hl / (rm * vm);
  const sx = ship.x / rs;
  const sy = ship.y / rs;
  const sz = ship.z / rs;
  const tx = hy * sz - hz * sy;
  const ty = hz * sx - hx * sz;
  const tz = hx * sy - hy * sx;
  const tl = Math.hypot(tx, ty, tz);
  if (!(tl > 0)) return false;
  out.x = speed * (radial * sx + (along * tx) / tl);
  out.y = speed * (radial * sy + (along * ty) / tl);
  out.z = speed * (radial * sz + (along * tz) / tl);
  return true;
}

/**
 * Radians a second a moon turns about its parent as its ephemeris flies it:
 * the angle about `pole` from `rel0` to `rel1`, taken `spanS` apart over about
 * one turn, plus that turn. The ephemeris keeps the moon's real period, which
 * a point-mass orbit at the moon's own energy does not: Phobos's runs 167 to
 * 213 km of track a turn ahead of one. NaN where there is no turn to read.
 */
export function sweptMeanMotion(
  rel0: FlightVec, pole: FlightVec, rel1: FlightVec, spanS: number,
): number {
  const pl = norm(pole);
  if (!(pl > 0) || !(spanS > 0)) return Number.NaN;
  const cx = rel0.y * rel1.z - rel0.z * rel1.y;
  const cy = rel0.z * rel1.x - rel0.x * rel1.z;
  const cz = rel0.x * rel1.y - rel0.y * rel1.x;
  const sin = (cx * pole.x + cy * pole.y + cz * pole.z) / pl;
  const cos = rel0.x * rel1.x + rel0.y * rel1.y + rel0.z * rel1.z;
  return (2 * Math.PI + Math.atan2(sin, cos)) / spanS;
}

/**
 * The semi-major axis about the parent a match parks the ship on beside a moon:
 * the period of the moon's own `meanMotionRadS`, shortened where the ship
 * `leads` the moon along its track and lengthened where it trails, so the ship
 * opens `driftM` along that track every turn of the moon, `radiusM` out.
 */
export function parkSemiMajorFor(
  gm: number, meanMotionRadS: number, radiusM: number, driftM: number, leads: boolean,
): number {
  if (!(gm > 0) || !(meanMotionRadS > 0) || !(radiusM > 0)) return Number.NaN;
  const n = meanMotionRadS * (1 + ((leads ? 1 : -1) * driftM) / (2 * Math.PI * radiusM));
  return Math.cbrt(gm / (n * n));
}

/** Where and on what orbit a match leaves the ship beside a moon. */
export interface MatchPark {
  /** Metres from the moon's centre. */
  readonly standoffM: number;
  /** Ahead of the moon along its track, rather than behind it. */
  readonly leads: boolean;
  /** The orbit about the parent the ship rides, or undefined for the moon's own energy. */
  readonly semiMajorM: number | undefined;
  /** The ship's distance from the parent over the moon's: that orbit's size over the moon's. */
  readonly scale: number;
}

/** Samples of the moon's distance from the parent over one turn, for the size
 *  of its orbit as the ephemeris draws it. */
const PARK_SIZE_SAMPLES = 64;

/**
 * The park a match onto `moonState`'s orbit leaves the ship in: ahead of the
 * moon where the capture pushes along the moon's motion (the ship comes in from
 * ahead) and behind it otherwise, on the moon's own shape of orbit sized to the
 * period `parkSemiMajorFor` names, read off the ephemeris over one turn from
 * `arrivalJd`. The ship sits `scale` of the moon's distance from the parent, so
 * it keeps the moon's shape instead of swinging round a smaller orbit from the
 * moon's height. `onMoonEnergy` parks on the moon's own track and energy instead.
 */
export function matchParkFor(
  gm: number, moonState: (jd: number, at: FlightVec, vel: FlightVec) => void, arrivalJd: number,
  captureDirection: FlightVec, standoffM: number, driftM: number, onMoonEnergy: boolean,
): MatchPark {
  const at = vec();
  const vel = vec();
  moonState(arrivalJd, at, vel);
  const leads = captureDirection.x * vel.x + captureDirection.y * vel.y + captureDirection.z * vel.z > 0;
  const onTrack: MatchPark = { standoffM, leads, semiMajorM: undefined, scale: 1 };
  const r = norm(at);
  const v = norm(vel);
  const a = 1 / (2 / r - (v * v) / gm);
  const periodS = 2 * Math.PI * Math.sqrt((a * a * a) / gm);
  if (onMoonEnergy || !(gm > 0) || !(periodS > 0)) return onTrack;
  const rel0 = vec();
  rel0.x = at.x;
  rel0.y = at.y;
  rel0.z = at.z;
  const pole = vec();
  pole.x = at.y * vel.z - at.z * vel.y;
  pole.y = at.z * vel.x - at.x * vel.z;
  pole.z = at.x * vel.y - at.y * vel.x;
  let low = Infinity;
  let high = 0;
  for (let i = 1; i <= PARK_SIZE_SAMPLES; i++) {
    moonState(arrivalJd + (periodS * i) / PARK_SIZE_SAMPLES / SECONDS_PER_DAY, at, vel);
    const ri = norm(at);
    if (ri < low) low = ri;
    if (ri > high) high = ri;
  }
  const semiMajorM = parkSemiMajorFor(gm, sweptMeanMotion(rel0, pole, at, periodS), r, driftM, leads);
  const size = (low + high) / 2;
  if (!Number.isFinite(semiMajorM) || !(size > 0)) return onTrack;
  return { standoffM, leads, semiMajorM, scale: semiMajorM / size };
}

/**
 * Where beside a moon a match parks the ship, relative to the parent: `park`'s
 * scale of the moon's own place, turned about the moon's orbital pole ahead of
 * the moon where the ship leads it and behind where it trails, as far as puts
 * it the standoff from the moon's centre. In the moon's plane, so the ship is
 * beside the moon and neither above nor below it. False where there is no such
 * place.
 */
export function parkPointFor(
  moonRel: FlightVec, moonRelVel: FlightVec, park: MatchPark, out: FlightVec,
): boolean {
  const r = norm(moonRel);
  const hx = moonRel.y * moonRelVel.z - moonRel.z * moonRelVel.y;
  const hy = moonRel.z * moonRelVel.x - moonRel.x * moonRelVel.z;
  const hz = moonRel.x * moonRelVel.y - moonRel.y * moonRelVel.x;
  const hl = Math.hypot(hx, hy, hz);
  if (!(r > 0) || !(hl > 0) || !(park.scale > 0)) return false;
  const rs = r * park.scale;
  const cos = Math.min(1, Math.max(-1,
    (r * r + rs * rs - park.standoffM * park.standoffM) / (2 * r * rs)));
  const turn = (park.leads ? 1 : -1) * Math.acos(cos);
  const c = Math.cos(turn) * park.scale;
  const s = (Math.sin(turn) * park.scale) / hl;
  out.x = moonRel.x * c + (hy * moonRel.z - hz * moonRel.y) * s;
  out.y = moonRel.y * c + (hz * moonRel.x - hx * moonRel.z) * s;
  out.z = moonRel.z * c + (hx * moonRel.y - hy * moonRel.x) * s;
  return true;
}

/** A moon's place and velocity relative to its parent `tS` seconds on. */
export type MoonTrack = (tS: number, at: FlightVec, vel: FlightVec) => void;

/** What `matchBrakeStartS` found, from the flight of the match it settled on. */
export interface MatchBrake {
  /** Seconds from now the throttle opens. */
  readonly startS: number;
  /** Metres from the moon's centre the match comes to rest. */
  readonly restRangeM: number;
  /** The park less where the match comes to rest, relative to the parent. */
  readonly miss: FlightVec;
  /** Where the ship coasts to `latestS` from now, relative to the parent. */
  readonly coastEnd: FlightVec;
  /** Unit: the ship's motion against the moon's there, the approach. */
  readonly approach: FlightVec;
  /** Metres a second of that motion. */
  readonly approachMps: number;
}

/** Simulated seconds a step of `matchBrakeStartS`'s burn, and at most one of its coast. */
const MATCH_BURN_STEP_S = 10;
const MATCH_COAST_STEP_S = 60;
/** Seconds the brake's start is found to, or the metres it stops from level with the park. */
const MATCH_START_TOLERANCE_S = 1;
const MATCH_LEVEL_TOLERANCE_M = 200;
/** Flights of the match `matchBrakeStartS` may fly before it takes the best start it has. */
const MATCH_MAX_FLIGHTS = 24;

/**
 * When a match onto a moon's orbit has to open its throttle for the ship to
 * come to rest at the park beside the moon (`parkPointFor`), in seconds from
 * now, found by flying the match itself from each candidate start: the parent's
 * pull, the throttle held full against what the ship still owes the orbit
 * `park` sizes (`coOrbitVelocityFor`), and the moon where `track` puts it. The
 * throttle opens where the place the match stops in stands level with the park
 * along the approach at `latestS`; `miss` is what is left across it. Half the
 * match before the arrival epoch holds only on a straight line, and the
 * parent's pull bends a match that runs a tenth of the moon's turn by tens of
 * kilometres. `shipRel` and `shipVel` are relative to the parent; `latestS` is
 * the latest start looked at.
 */
export function matchBrakeStartS(
  gm: number, track: MoonTrack, shipRel: FlightVec, shipVel: FlightVec, accelMps2: number,
  park: MatchPark, unrotated: boolean, latestS: number, stopMps: number,
): MatchBrake | null {
  if (!(gm > 0) || !(accelMps2 > 0) || !(latestS >= 0)) return null;
  const moonAt = vec();
  const moonVel = vec();
  const want = vec();
  const parkAt = vec();
  const ship = vec();
  const count = Math.max(1, Math.ceil(latestS / MATCH_COAST_STEP_S));
  const h = latestS / count;
  const k1 = new Float64Array(6);
  const k2 = new Float64Array(6);
  const k3 = new Float64Array(6);
  const k4 = new Float64Array(6);
  const y = new Float64Array(6);
  const deriv = (s: Float64Array, ax: number, ay: number, az: number, out: Float64Array): void => {
    const r2 = s[0]! * s[0]! + s[1]! * s[1]! + s[2]! * s[2]!;
    const g = -gm / (r2 * Math.sqrt(r2));
    out[0] = s[3]!;
    out[1] = s[4]!;
    out[2] = s[5]!;
    out[3] = g * s[0]! + ax;
    out[4] = g * s[1]! + ay;
    out[5] = g * s[2]! + az;
  };
  const step = (s: Float64Array, dt: number, ax: number, ay: number, az: number): void => {
    deriv(s, ax, ay, az, k1);
    for (let i = 0; i < 6; i++) y[i] = s[i]! + (dt / 2) * k1[i]!;
    deriv(y, ax, ay, az, k2);
    for (let i = 0; i < 6; i++) y[i] = s[i]! + (dt / 2) * k2[i]!;
    deriv(y, ax, ay, az, k3);
    for (let i = 0; i < 6; i++) y[i] = s[i]! + dt * k3[i]!;
    deriv(y, ax, ay, az, k4);
    for (let i = 0; i < 6; i++) s[i] = s[i]! + (dt / 6) * (k1[i]! + 2 * k2[i]! + 2 * k3[i]! + k4[i]!);
  };
  // The coast, once: every candidate start opens off it.
  const coast = new Float64Array((count + 1) * 6);
  const s = Float64Array.of(shipRel.x, shipRel.y, shipRel.z, shipVel.x, shipVel.y, shipVel.z);
  coast.set(s, 0);
  for (let k = 1; k <= count; k++) {
    step(s, h, 0, 0, 0);
    coast.set(s, k * 6);
  }
  const coastEnd = vec();
  coastEnd.x = s[0]!;
  coastEnd.y = s[1]!;
  coastEnd.z = s[2]!;
  track(latestS, moonAt, moonVel);
  const approach = vec();
  approach.x = s[3]! - moonVel.x;
  approach.y = s[4]! - moonVel.y;
  approach.z = s[5]! - moonVel.z;
  const al = norm(approach);
  if (!(al > 0)) return null;
  approach.x /= al;
  approach.y /= al;
  approach.z /= al;
  interface Rest { startS: number; offsetM: number; rangeM: number; miss: FlightVec }
  // Where the match opened `startS` from now comes to rest, measured along the
  // approach from the park: below zero it stops short, above it runs past.
  const restFrom = (startS: number): Rest | null => {
    const k = Math.min(count, Math.floor(startS / h));
    for (let i = 0; i < 6; i++) s[i] = coast[k * 6 + i]!;
    if (startS > k * h) step(s, startS - k * h, 0, 0, 0);
    let t = startS;
    for (let guard = 0; guard < 100_000; guard++) {
      track(t, moonAt, moonVel);
      ship.x = s[0]!;
      ship.y = s[1]!;
      ship.z = s[2]!;
      if (!coOrbitVelocityFor(gm, moonAt, moonVel, ship, unrotated, want, park.semiMajorM)) {
        return null;
      }
      const dx = want.x - s[3]!;
      const dy = want.y - s[4]!;
      const dz = want.z - s[5]!;
      const gap = Math.sqrt(dx * dx + dy * dy + dz * dz);
      if (gap <= stopMps) break;
      const dt = Math.min(MATCH_BURN_STEP_S, gap / accelMps2);
      step(s, dt, (accelMps2 * dx) / gap, (accelMps2 * dy) / gap, (accelMps2 * dz) / gap);
      t += dt;
    }
    track(t, moonAt, moonVel);
    if (!parkPointFor(moonAt, moonVel, park, parkAt)) return null;
    const miss = vec();
    miss.x = parkAt.x - s[0]!;
    miss.y = parkAt.y - s[1]!;
    miss.z = parkAt.z - s[2]!;
    const rx = s[0]! - moonAt.x;
    const ry = s[1]! - moonAt.y;
    const rz = s[2]! - moonAt.z;
    return {
      startS,
      offsetM: -(miss.x * approach.x + miss.y * approach.y + miss.z * approach.z),
      rangeM: Math.sqrt(rx * rx + ry * ry + rz * rz),
      miss,
    };
  };
  const found = (rest: Rest): MatchBrake => ({
    startS: rest.startS, restRangeM: rest.rangeM, miss: rest.miss, coastEnd, approach,
    approachMps: al,
  });
  // The bracket opens round half the match before `latestS` and widens from
  // there: a match flown from hours out is the longest flight there is.
  ship.x = coastEnd.x;
  ship.y = coastEnd.y;
  ship.z = coastEnd.z;
  if (!coOrbitVelocityFor(gm, moonAt, moonVel, ship, unrotated, want, park.semiMajorM)) return null;
  const matchS = Math.hypot(want.x - s[3]!, want.y - s[4]!, want.z - s[5]!) / accelMps2;
  let span = Math.max(MATCH_COAST_STEP_S, matchS / 8);
  let flights = 1;
  const first = restFrom(Math.min(latestS, Math.max(0, latestS - matchS / 2)));
  if (first === null) return null;
  let lo = first;
  let hi = first;
  while (lo.offsetM >= 0 || hi.offsetM < 0) {
    if (flights++ >= MATCH_MAX_FLIGHTS) return found(first);
    const reach = lo.offsetM >= 0 ? lo.startS - span : hi.startS + span;
    if (reach < 0 && lo.startS <= 0) return found(lo);
    if (reach > latestS && hi.startS >= latestS) return found(hi);
    const next = restFrom(Math.min(latestS, Math.max(0, reach)));
    if (next === null) return null;
    if (lo.offsetM >= 0) {
      if (next.offsetM >= 0) hi = next;
      lo = next;
    } else {
      if (next.offsetM < 0) lo = next;
      hi = next;
    }
    span *= 2;
  }
  // Regula falsi, with the end that keeps its place halved each time (Illinois).
  let loWeight = 1;
  let hiWeight = 1;
  let best = -lo.offsetM < hi.offsetM ? lo : hi;
  for (; flights < MATCH_MAX_FLIGHTS && hi.startS - lo.startS > MATCH_START_TOLERANCE_S; flights++) {
    if (Math.abs(best.offsetM) <= MATCH_LEVEL_TOLERANCE_M) break;
    const fl = lo.offsetM * loWeight;
    const fh = hi.offsetM * hiWeight;
    const at = lo.startS + ((hi.startS - lo.startS) * fl) / (fl - fh);
    const mid = restFrom(Math.min(hi.startS, Math.max(lo.startS, at)));
    if (mid === null) return null;
    if (Math.abs(mid.offsetM) < Math.abs(best.offsetM)) best = mid;
    if (mid.offsetM < 0) {
      lo = mid;
      loWeight = 1;
      hiWeight /= 2;
    } else {
      hi = mid;
      hiWeight = 1;
      loWeight /= 2;
    }
  }
  return found(best);
}

/** The aim measured from the centre instead, which is what the guidance flew
 *  before and what `--inject aim-through-centre` puts back. */
export function centreStandoffFor(
  destinationRadiusM: number, parkingAltitudeM = ARRIVAL_STANDOFF_M,
): number {
  if (!(destinationRadiusM > 0)) return parkingAltitudeM;
  return Math.min(parkingAltitudeM, ARRIVAL_STANDOFF_RADII * destinationRadiusM);
}

// A burn opens its throttle within this angle of its direction, or the arc one
// frame's slew covers: a fixed tolerance deadlocks a direction that is turning.
const ALIGN_TOLERANCE_RAD = 1e-3;

/** A nose further than this off its burn direction is a turn to watch, not one to track: it turns on real seconds and the clock drops under it. */
const SLEW_FRAME_RAD = 2 * Math.PI / 180;

/** Metres a second below which a trim is not worth a slew. The delta-v map's
 *  own COURSE FIX row reads nothing under it, so the panel and the autopilot
 *  call a course straight at the same place. */
export const TRIM_DEADBAND_MPS = 0.02;

/** Metres a second a burn stops at rather than chasing the last bit. */
const BURN_STOP_MPS = 1e-3;

/** The same for a steered departure, which stops on the difference from the
 *  conic it wants: above the frame's discretisation, which 1e-3 would chase. */
const DEPARTURE_STOP_MPS = 0.05;

/** Metres a second one frame of a burn may spend, which is what keeps a warped
 *  frame from putting a whole burn along one frozen direction. */
const BURN_DV_PER_FRAME_MPS = 40;

/** A burn's step is held under this fraction of the local orbital period, so
 *  the nose can track a direction the orbit itself is turning. */
const BURN_FRAME_PERIOD_FRACTION = 1 / 400;

/**
 * Guidance steps one frame of a burn may be split into.
 *
 * The step above is what the burn must be re-aimed and re-integrated at; the
 * clock's ceiling during a burn is however many of those steps a frame can
 * carry. Thirty-two of them is 1 HR/S out of a 400 km parking orbit (a 13.9 s
 * step against the 445 s a frame buys there at the caller's own clamped frame)
 * and 1 HR/S again in deep space, where the 40 m/s a step may spend is what
 * binds instead - so the burn's ceiling is one rung under the ladder's top
 * wherever the ship is, which is a thing the panel can say in one line.
 */
export const BURN_STEPS_PER_FRAME = 32;

/** The most of the trip one frame may fly, at whatever warp. A frame is how
 *  finely anything between two of them can be dated - a rung, the arrival, the
 *  pass - and an hour a second is a minute a frame: nothing across an AU and
 *  most of the last leg of a flight to a moon. */
const FRAME_TRIP_FRACTION = 1e-3;

/** Real seconds of slew a burn is given before its own start epoch. */
const ALIGN_LEAD_REAL_S = 6;

/** The most of the trip one nose turn may cost. The turn takes real seconds and
 *  the clock does not: six of them at an hour a second is six hours of sim,
 *  which is a lead on a flight of months and most of a flight to a moon. It
 *  bounds both the lead a dated burn opens with and the warp a turn already
 *  under way may run at. */
const SLEW_TRIP_FRACTION = 0.005;

/** The most of what is LEFT of the trip one nose turn may cost. The trip-wide
 *  budget is 26 hours on a crossing of months, which the last rung does not
 *  have: an hour out, a half-second turn at an hour a second flew the ship
 *  14,000 km past the state its own correction was solved from. */
const SLEW_LEFT_FRACTION = 0.01;

/** Real seconds of lead a coast keeps in hand when it picks a warp level. */
const WARP_LEAD_REAL_S = 8;

// Trim rungs as a fraction of the time of flight still to run: a Hohmann has no
// out-of-plane response at departure and none cheap at the end.
const TRIM_TGO_FRACTIONS: readonly number[] = [0.98, 0.6, 0.35, 0.15, 0.05, 0.015];

// The last two rungs, simulated seconds before arrival, where the model and the
// flight agree closely enough for one more correction to be worth flying.
const FINAL_TRIM_TGO_S: readonly number[] = [6 * 3600, 3600];

/** Newton steps one required-velocity solve may take. */
const SOLVE_MAX_ITERATIONS = 12;
/** Metres the solve closes the predicted miss to before it stops. */
const SOLVE_TOLERANCE_M = 50;
/** Metres a second the Jacobian is differenced with. */
const SOLVE_PROBE_MPS = 1e-3;
/** Metres a second one Newton step may move the velocity. */
const SOLVE_STEP_CEILING_MPS = 2000;
/** Halvings a step gets before the search calls the iteration finished. */
const SOLVE_BACKTRACKS = 8;

// The guidance's own step: a fraction of the local two-body timescale, floored
// on steps per span so a six-hour span is fine enough to match the flight.
const MODEL_STEP_FRACTION = 1e-3;
const MODEL_STEPS_PER_SPAN = 2000;
const MODEL_STEP_MIN_S = 0.05;
const MODEL_STEP_MAX_S = 20000;
/** Steps one propagation may take before the solve gives up. */
const MODEL_STEP_CEILING = 200000;

// Integrator steps one update() spends on a solve before handing the frame back.
// The upper bound on a slice; SOLVE_MS_BUDGET is what usually ends one.
export const SOLVE_STEP_BUDGET = 800;

// Milliseconds an update that runs a slice may take, counted from the top of the
// update and checked inside the step loop. The frames a solve is spread over fly
// nothing, so spreading it wider costs the trip no accuracy.
export const SOLVE_MS_BUDGET = 8;

/** Milliseconds a slice gets when the call has spent its budget before the solve
 *  runs: the steps up to the first clock read, so a solve still moves every call. */
const SLICE_MIN_MS = 1e-3;

/** A landed solve seeded further than this ahead of its rung's date is opened
 *  again from nearer: the coast predicted to the date misses what the reduced
 *  world leaves out, and that grows with how far out it was seeded. */
const AHEAD_REFRESH_S = 6 * 3600;
/** Metres an ahead solve's start position is moved by to probe how its arrival follows it. */
const AHEAD_PROBE_M = 1e6;
/** An ahead slice's step budget over a frozen one's: the milliseconds still end
 *  it, and it has to land inside the coast's few real seconds. */
const AHEAD_STEP_FACTOR = 1.5;

/** A course fix's solve opened ahead of its date: the rung it is for, the date
 *  it was seeded at, and where it predicted the ship at the rung. */
interface AheadSolve {
  readonly idx: number;
  readonly seedJd: number;
  readonly at: FlightVec;
  readonly solve: GuidanceSolve;
}

/** Steps of a finite capture's climb flown ahead on each coasting frame, under a
 *  millisecond before the loop is compiled, and the margin on the plan's excess
 *  speed they are flown to: the approach the capture is re-priced on sits within
 *  a few percent of it. */
const CLIMB_WARM_STEPS = 1024;
const CLIMB_WARM_VINF_FACTOR = 1.25;

/** Steps between clock reads inside a slice: the read itself is not free. */
const SLICE_CLOCK_STEPS = 32;

/** Bisection steps the departure's flight path angle is solved to. The count
 *  `src/sim/transfer.ts` prices the same conic with. */
const DEPARTURE_BISECTION_STEPS = 60;

const TWO_PI = Math.PI * 2;

/** What a steered departure may spend before it calls the plan wrong, over the
 *  instant push: the climb at this drive is a fraction of that price, never a
 *  multiple of it, so the ceiling is read off `impulseDvKmS` and does not move
 *  when the map prices the burn length into the row. */
const DEPARTURE_CEILING_FACTOR = 2.5;

/** Lean of a bound departure's planned push off the line of the ship's motion
 *  past which it is steered onto the plan's conic rather than bought on energy
 *  along that motion. A Mars moon push planned along it leans 0.5 to 1.4 deg;
 *  one planned with a radial part from 17,038.5 km up leans 64 to 77. */
const BOUND_PUSH_ALONG_MOTION_RAD = (10 * Math.PI) / 180;
/** Share of the ship's own orbit past which a bound departure that turns is
 *  flown on energy and its turn left to the course fixes. Centred on the plan's
 *  burn point, a longer push opens over a quarter turn from it, where the
 *  Lambert solve to the arrival is no longer the plan's conic: Luna from 400 km
 *  at 0.47 m/s2 is 1.2 orbits, and that solve asks 11 km/s and brakes into Earth.
 *  Reaches Luna and every Jupiter, Saturn, Uranus and Neptune moon from a 400 km
 *  circle; no Mars moon. */
export const TURN_STEER_MAX_ORBIT_SHARE = 0.5;

/** Whether a planned departure push leans off the line of the ship's motion by
 *  more than BOUND_PUSH_ALONG_MOTION_RAD, forward or back. Bought on energy
 *  along the motion, such a push lands on an ellipse of the right size and the
 *  wrong shape, and the course fixes pay for the difference. */
export function pushLeansOffMotion(lb: LocalBurnPoint | null): boolean {
  if (lb === null) return false;
  const v = norm(lb.velocityKmS);
  const t = norm(lb.thrustDirection);
  if (!(v > 0) || !(t > 0)) return false;
  const d = lb.thrustDirection;
  const along = Math.abs(d.x * lb.velocityKmS.x + d.y * lb.velocityKmS.y + d.z * lb.velocityKmS.z) / (v * t);
  return along < Math.cos(BOUND_PUSH_ALONG_MOTION_RAD);
}

const DEFAULT_TRIM_CEILING_MPS = 4000;
const DEFAULT_TRIM_BUDGET_MPS = 12000;
const DEFAULT_FPS = 60;

const ZERO_AXES: FlightAxes = { pitch: 0, yaw: 0, roll: 0 };

function vec(): FlightVec {
  return { x: 0, y: 0, z: 0 };
}

function norm(v: FlightVec): number {
  return Math.hypot(v.x, v.y, v.z);
}

/** `q^-1 * v`: a direction in the flight frame, read in the ship's own axes. */
function rotateIntoBody(q: FlightQuat, v: FlightVec, out: FlightVec): FlightVec {
  const { x, y, z, w } = q;
  const tx = 2 * (-y * v.z + z * v.y);
  const ty = 2 * (-z * v.x + x * v.z);
  const tz = 2 * (-x * v.y + y * v.x);
  out.x = v.x + w * tx + (-y * tz + z * ty);
  out.y = v.y + w * ty + (-z * tx + x * tz);
  out.z = v.z + w * tz + (-x * ty + y * tx);
  return out;
}

// The held axes that bring the nose onto `target` and the angle it is off: the
// shortest arc, the rates scaled together so a multi-frame turn keeps its axis.
export function steerAxes(
  state: FlightState, target: FlightVec, turnRateRadS: number, attitudeDtS: number,
  out: FlightAxes,
): number {
  out.pitch = 0;
  out.yaw = 0;
  out.roll = 0;
  const nose = flightNose(state, _nose);
  const tl = norm(target);
  if (!(tl > 0)) return 0;
  const tx = target.x / tl;
  const ty = target.y / tl;
  const tz = target.z / tl;
  _axis.x = nose.y * tz - nose.z * ty;
  _axis.y = nose.z * tx - nose.x * tz;
  _axis.z = nose.x * ty - nose.y * tx;
  const s = norm(_axis);
  const angle = Math.atan2(s, nose.x * tx + nose.y * ty + nose.z * tz);
  if (!(angle > 0)) return 0;
  if (!(s > 0)) {
    // Exactly reversed: no plane to turn in, so pitch until there is one.
    out.pitch = 1;
    return angle;
  }
  if (!(turnRateRadS > 0) || !(attitudeDtS > 0)) return angle;
  rotateIntoBody(state.orientation, _axis, _body);
  const bl = norm(_body);
  if (!(bl > 0)) return angle;
  const reach = turnRateRadS * attitudeDtS;
  const scale = Math.min(1, angle / reach) / bl;
  out.pitch = _body.x * scale;
  out.yaw = _body.y * scale;
  out.roll = _body.z * scale;
  return angle;
}

const _nose: FlightVec = vec();
const _axis: FlightVec = vec();
const _body: FlightVec = vec();

function clampUnit(x: number): number {
  return x < -1 ? -1 : x > 1 ? 1 : x;
}

/** Angle from the ship's radius round to the outgoing asymptote of the conic a
 *  velocity at flight path angle `gammaRad` puts it on. Falls from 2pi at -pi/2
 *  to 0 at +pi/2, so one root. `src/sim/transfer.ts` solves the same function. */
function asymptoteSweepRad(
  gammaRad: number, gm: number, radiusM: number, speedMps: number, vInfMps: number,
): number {
  const momentum = radiusM * speedMps * Math.cos(gammaRad);
  const e = Math.sqrt(1 + (vInfMps * vInfMps * momentum * momentum) / (gm * gm));
  const toAsymptote = Math.acos(clampUnit(-1 / e));
  const trueAnomaly = Math.acos(
    clampUnit(((momentum * momentum) / (gm * radiusM) - 1) / e),
  );
  return toAsymptote - (gammaRad < 0 ? -trueAnomaly : trueAnomaly);
}

function solveFlightPathRad(
  targetRad: number, gm: number, radiusM: number, speedMps: number, vInfMps: number,
): number {
  let lo = -Math.PI / 2;
  let hi = Math.PI / 2;
  for (let i = 0; i < DEPARTURE_BISECTION_STEPS; i++) {
    const mid = (lo + hi) / 2;
    if (asymptoteSweepRad(mid, gm, radiusM, speedMps, vInfMps) > targetRad) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

// The origin-relative velocity at `rel` on the escape conic whose asymptote lies
// along `asymptote` with excess speed `vInf`; false where no such conic exists.
export function departureRequiredVelocity(
  rel: FlightVec, relVel: FlightVec, gm: number, vInfMps: number,
  asymptote: FlightVec, out: FlightVec,
): boolean {
  const radiusM = norm(rel);
  const aimLen = norm(asymptote);
  if (!(radiusM > 0) || !(gm > 0) || !(aimLen > 0)) return false;
  const speed = Math.sqrt(vInfMps * vInfMps + (2 * gm) / radiusM);
  const ox = rel.x / radiusM;
  const oy = rel.y / radiusM;
  const oz = rel.z / radiusM;
  const ax = asymptote.x / aimLen;
  const ay = asymptote.y / aimLen;
  const az = asymptote.z / aimLen;
  // The conic's plane. Where the radius and the asymptote are parallel there is
  // none, and the ship's own orbit plane is the one it leaves along.
  let px = oy * az - oz * ay;
  let py = oz * ax - ox * az;
  let pz = ox * ay - oy * ax;
  let pl = Math.hypot(px, py, pz);
  if (!(pl > 1e-9)) {
    px = oy * relVel.z - oz * relVel.y;
    py = oz * relVel.x - ox * relVel.z;
    pz = ox * relVel.y - oy * relVel.x;
    pl = Math.hypot(px, py, pz);
    if (!(pl > 0)) return false;
  }
  px /= pl;
  py /= pl;
  pz /= pl;
  const sweepRad = Math.acos(clampUnit(ox * ax + oy * ay + oz * az));
  let bestDv = Infinity;
  for (const way of [1, -1]) {
    const gammaRad = solveFlightPathRad(
      way > 0 ? sweepRad : TWO_PI - sweepRad, gm, radiusM, speed, vInfMps);
    const tx = way * (py * oz - pz * oy);
    const ty = way * (pz * ox - px * oz);
    const tz = way * (px * oy - py * ox);
    const radial = speed * Math.sin(gammaRad);
    const transverse = speed * Math.cos(gammaRad);
    const vx = ox * radial + tx * transverse;
    const vy = oy * radial + ty * transverse;
    const vz = oz * radial + tz * transverse;
    const dv = Math.hypot(vx - relVel.x, vy - relVel.y, vz - relVel.z);
    if (!(dv < bestDv)) continue;
    bestDv = dv;
    out.x = vx;
    out.y = vy;
    out.z = vz;
  }
  return Number.isFinite(bestDv);
}

/** Rounds the departure's start is solved over, and the exit angle it stops at. */
const DEPARTURE_TIMING_ROUNDS = 12;
const DEPARTURE_TIMING_TOL_RAD = 1e-4;
/** The climb's integration step for the timing, of the parking period: the
 *  exit lands within 0.03 deg of the planner's half-second step at about a
 *  third of the cost, and the swing after the climb takes up the rest. */
const DEPARTURE_TIMING_STEP_FRACTION = 1 / 4000;
/** How far the timed start may slip off the schedule's, of the time of flight.
 *  An orbit knocked near escape since the plan reads a period of years. */
const DEPARTURE_TIMING_SLIP_FRACTION = 0.02;
/** How far ahead of the start it is timed at the least: past the day short of
 *  the window a skip stops at, so the count the skip lands on is the timed one. */
export const DEPARTURE_TIMING_REACH_S = 2 * 86400;

/** `r0`, `v0` coasted `dtS` along their own ellipse about `gm`, by the f and g
 *  functions of the eccentric anomaly. False off a closed orbit. */
export function keplerAfter(
  gm: number, r0: FlightVec, v0: FlightVec, dtS: number, outR: FlightVec, outV: FlightVec,
): boolean {
  const r = norm(r0);
  const vSq = v0.x * v0.x + v0.y * v0.y + v0.z * v0.z;
  if (!(gm > 0) || !(r > 0)) return false;
  const inverseA = 2 / r - vSq / gm;
  if (!(inverseA > 0)) return false;
  const a = 1 / inverseA;
  const n = Math.sqrt(gm / (a * a * a));
  const period = TWO_PI / n;
  const dt = dtS - Math.floor(dtS / period) * period;
  const sigma = (r0.x * v0.x + r0.y * v0.y + r0.z * v0.z) / Math.sqrt(gm);
  const sqrtA = Math.sqrt(a);
  const eSin0 = sigma / sqrtA;
  const eCos0 = 1 - r / a;
  const m = n * dt;
  let dE = m;
  for (let i = 0; i < 50; i++) {
    const step = (dE + eSin0 * (1 - Math.cos(dE)) - eCos0 * Math.sin(dE) - m)
      / (1 + eSin0 * Math.sin(dE) - eCos0 * Math.cos(dE));
    dE -= step;
    if (Math.abs(step) < 1e-13) break;
  }
  const c = Math.cos(dE);
  const s = Math.sin(dE);
  const rNow = a + (r - a) * c + sigma * sqrtA * s;
  const f = 1 - (a / r) * (1 - c);
  const g = ((a * sigma) / Math.sqrt(gm)) * (1 - c) + r * Math.sqrt(a / gm) * s;
  const fDot = (-Math.sqrt(gm * a) / (rNow * r)) * s;
  const gDot = 1 - (a / rNow) * (1 - c);
  const rx = f * r0.x + g * v0.x;
  const ry = f * r0.y + g * v0.y;
  const rz = f * r0.z + g * v0.z;
  outV.x = fDot * r0.x + gDot * v0.x;
  outV.y = fDot * r0.y + gDot * v0.y;
  outV.z = fDot * r0.z + gDot * v0.z;
  outR.x = rx;
  outR.y = ry;
  outR.z = rz;
  return true;
}

/** Unit outgoing asymptote of the hyperbola `rel`, `relVel` are on about `gm`.
 *  False off a hyperbola. */
export function outgoingAsymptote(
  rel: FlightVec, relVel: FlightVec, gm: number, out: FlightVec,
): boolean {
  const r = norm(rel);
  if (!(r > 0) || !(gm > 0)) return false;
  const hx = rel.y * relVel.z - rel.z * relVel.y;
  const hy = rel.z * relVel.x - rel.x * relVel.z;
  const hz = rel.x * relVel.y - rel.y * relVel.x;
  const hl = Math.hypot(hx, hy, hz);
  if (!(hl > 0)) return false;
  const ex = (relVel.y * hz - relVel.z * hy) / gm - rel.x / r;
  const ey = (relVel.z * hx - relVel.x * hz) / gm - rel.y / r;
  const ez = (relVel.x * hy - relVel.y * hx) / gm - rel.z / r;
  const e = Math.hypot(ex, ey, ez);
  if (!(e > 1)) return false;
  const px = ex / e;
  const py = ey / e;
  const pz = ez / e;
  const k = Math.sqrt(e * e - 1);
  const qx = (hy * pz - hz * py) / hl;
  const qy = (hz * px - hx * pz) / hl;
  const qz = (hx * py - hy * px) / hl;
  out.x = (-px + k * qx) / e;
  out.y = (-py + k * qy) / e;
  out.z = (-pz + k * qz) / e;
  return true;
}

/** One burn in hand: where it points, what it still owes, when it may start. */
interface BurnOrder {
  readonly label: string;
  /** Re-read every frame for a departure, held for a trim or a capture. */
  readonly steered: boolean;
  direction: FlightVec;
  dvToGoMps: number;
  /** Target specific energy relative to the origin, J/kg, for a steered
   *  departure; NaN where the burn is a fixed amount of delta-v. Negative on a
   *  departure onto a bound ellipse, which is the energy of that ellipse. */
  targetEnergy: number;
  /** The plan's own excess speed, m/s, for a steered departure; 0 otherwise. */
  targetVInf: number;
  /** The departure stays inside the origin's well, so the burn is over when the
   *  ship's speed about it is the transfer ellipse's own and there is no
   *  asymptote to point at afterwards. */
  bound: boolean;
  /** Unit normal of the plane the plan's own push leaves a bound departure in,
   *  turn included; null where the burn is flown on energy along the ship's motion. */
  boundNormal: FlightVec | null;
  /** A steered departure that has bought its energy and is now on the second
   *  law, putting the asymptote on the plan's own. */
  pointing: boolean;
  startJd: number;
  spentMps: number;
  /** Metres a second the burn stops itself at, whatever its own rule says. */
  ceilingMps: number;
  /** How a capture is steered and stopped, re-read every frame: `orbit` against
   *  the motion about the destination until the ship has the parking orbit's
   *  energy, `match` onto the destination's own orbit about its parent. Null on
   *  every other burn and on a capture that runs down a fixed push. */
  law: 'orbit' | 'match' | null;
  /** Share of the drive this frame has that the burn asks for; 1 where absent. */
  throttleShare?: number;
  /** A capture into orbit whose last stage is under way: the velocity of a
   *  circle at the radius the ship stands at, which is what ends it clear of
   *  the ground wherever the stage before it left the ship. */
  circularize?: boolean;
  /** A profile capture whose throttle has opened. */
  opened?: boolean;
}

/** A capture may spend this many times its priced figure before the autopilot
 *  gives up on it rather than burn the tank on an approach gone wrong. */
const CAPTURE_CEILING_FACTOR = 2;

/** Of the drive, the share a profile capture plans its stop on. The rest is
 *  what a late start or a drive giving less than it was asked for is absorbed
 *  from: at 8 km/s and 0.49 m/s2, 24 minutes of lateness. */
const CAPTURE_PROFILE_SHARE = 0.85;

/** Gravity at the parking orbit under this share of the drive makes the
 *  capture a profile capture: the drive, not the pull, shapes the stop. */
const PROFILE_WELL_SHARE = 0.25;

/** Seconds of the drive a profile capture's speed above the circle's may be
 *  down to before it hands over to the circle. */
const PROFILE_TERMINAL_S = 30;

/** Share of the drive a profile capture may put across its motion to move
 *  where it passes the destination. */
const PROFILE_LATERAL_SHARE = 0.5;

/** A capture into orbit where the destination's pull at the parking orbit is
 *  weak against the drive: Pallas, Ceres, Vesta and the small moons. Thrust
 *  held against the motion down to the parking orbit's energy ends wherever the
 *  start put it there, and a minute late at 8 km/s is 480 km; this capture is
 *  instead flown onto the point the parking orbit touches the ship's own line. */
export interface ProfileCapture {
  readonly gm: number;
  readonly radiusM: number;
  /** Circular speed at the parking radius, m/s. */
  readonly circularMps: number;
}

export function profileCaptureOf(
  finite: FiniteCapture | null | undefined, accelMps2: number,
): ProfileCapture | null {
  if (finite === null || finite === undefined || finite.gmKm3S2 === undefined) return null;
  const gm = finite.gmKm3S2 * 1e9;
  const radiusM = finite.parkingRadiusKm * 1000;
  if (!(gm > 0) || !(radiusM > 0) || !(accelMps2 > 0)) return null;
  if (!(gm / (radiusM * radiusM) < PROFILE_WELL_SHARE * accelMps2)) return null;
  return { gm, radiusM, circularMps: Math.sqrt(gm / radiusM) };
}

/** Seconds ahead of the closest approach a ship coasting in at `speedMps`
 *  opens a profile capture's throttle, on CAPTURE_PROFILE_SHARE of `accelMps2`. */
export function profileLeadS(speedMps: number, endMps: number, accelMps2: number): number {
  if (!(speedMps > 0) || !(accelMps2 > 0)) return 0;
  return (speedMps * speedMps - endMps * endMps)
    / (2 * CAPTURE_PROFILE_SHARE * accelMps2 * speedMps);
}

/** Seconds of burn left under which a guided capture flies the tilt it has
 *  rather than solving again: the miss a tilt can still move goes as the square
 *  of the time left, so the solve turns singular at the cut. */
export const CAPTURE_GUIDE_HOLD_S = 120;

/** Largest tilt off the retrograde a guided capture takes. A timing error the
 *  size of a minute needs a few tenths of a degree. */
const CAPTURE_GUIDE_MAX_RAD = 0.3;

/** Tilt of an energy-law capture's thrust off the retrograde, in the plane of
 *  the motion and toward the side the ship is turning, as `tiltRad + rateRadS
 *  * t` seconds on from now; `burnS` is the burn left under it. */
export interface CaptureTilt {
  readonly tiltRad: number;
  readonly rateRadS: number;
  readonly burnS: number;
}

interface TiltedCut {
  /** Off the circle the target energy names: radius over its own, and
   *  radial speed over its circular speed. */
  readonly radiusMiss: number;
  readonly radialMiss: number;
  readonly burnS: number;
}

/** The rest of an energy-law capture flown in the plane of its own motion on a
 *  tilt, two-body at `accel`, to the moment its energy falls to `targetEnergy`. */
function flyTiltedCapture(
  gm: number, r0: number, vr0: number, vt0: number, accel: number, targetEnergy: number,
  tiltRad: number, rateRadS: number, stepS: number, capS: number,
): TiltedCut | null {
  const a = -gm / (2 * targetEnergy);
  const circular = Math.sqrt(gm / a);
  let x = r0;
  let y = 0;
  let vx = vr0;
  let vy = vt0;
  let energy = (vx * vx + vy * vy) / 2 - gm / r0;
  for (let t = 0; t < capS; t += stepS) {
    const r = Math.hypot(x, y);
    const v = Math.hypot(vx, vy);
    if (!(r > 0) || !(v > 0)) return null;
    const th = tiltRad + rateRadS * t;
    const c = Math.cos(th) / v;
    const s = Math.sin(th) / v;
    const g = -gm / (r * r * r);
    const nvx = vx + (g * x + accel * (-c * vx - s * vy)) * stepS;
    const nvy = vy + (g * y + accel * (-c * vy + s * vx)) * stepS;
    const nx = x + nvx * stepS;
    const ny = y + nvy * stepS;
    const nr = Math.hypot(nx, ny);
    const next = (nvx * nvx + nvy * nvy) / 2 - gm / nr;
    if (next <= targetEnergy) {
      const f = energy > next ? (energy - targetEnergy) / (energy - next) : 1;
      const rCut = r + f * (nr - r);
      const radial = (x * vx + y * vy) / r;
      const radialNext = (nx * nvx + ny * nvy) / nr;
      return {
        radiusMiss: (rCut - a) / a,
        radialMiss: (radial + f * (radialNext - radial)) / circular,
        burnS: t + f * stepS,
      };
    }
    x = nx;
    y = ny;
    vx = nvx;
    vy = nvy;
    energy = next;
  }
  return null;
}

/**
 * The tilt that ends an energy-law capture on the circle its target energy
 * names. Held along the retrograde, the capture ends on that energy wherever
 * the burn's start and the frame's integration left it: a brake opened 20 s
 * off its price hands back an orbit tens of kilometres out of round. Cut on
 * the energy, a circle is two more conditions, the radius and no radial speed,
 * and the tilt and its rate are the two things solved for, by Newton on the
 * rest of the burn flown two-body from the ship's own state. Null where the
 * solve does not close or asks for more tilt than CAPTURE_GUIDE_MAX_RAD.
 */
export function guidedCaptureTilt(
  gm: number, rel: FlightVec, relVel: FlightVec, accelMps2: number, targetEnergy: number,
  guessTiltRad: number, guessRateRadS: number, iterations: number,
): CaptureTilt | null {
  const r0 = norm(rel);
  const v0 = norm(relVel);
  if (!(gm > 0) || !(r0 > 0) || !(v0 > 0) || !(accelMps2 > 0) || !(targetEnergy < 0)) return null;
  const vr0 = (rel.x * relVel.x + rel.y * relVel.y + rel.z * relVel.z) / r0;
  const vt0 = Math.sqrt(Math.max(0, v0 * v0 - vr0 * vr0));
  const excess = (v0 * v0) / 2 - gm / r0 - targetEnergy;
  if (!(excess > 0) || !(vt0 > 0)) return null;
  const roughS = (v0 - Math.sqrt(Math.max(0, v0 * v0 - 2 * excess))) / accelMps2;
  const stepS = Math.min(5, Math.max(0.05, roughS / 1000));
  const capS = 3 * roughS + 600;
  let tilt = guessTiltRad;
  let rate = guessRateRadS;
  let cut = flyTiltedCapture(gm, r0, vr0, vt0, accelMps2, targetEnergy, tilt, rate, stepS, capS);
  for (let i = 0; i < iterations && cut !== null; i++) {
    if (Math.hypot(cut.radiusMiss, cut.radialMiss) < 1e-7) break;
    const dTilt = 1e-4;
    const dRate = dTilt / Math.max(cut.burnS, 1);
    const byTilt = flyTiltedCapture(gm, r0, vr0, vt0, accelMps2, targetEnergy,
      tilt + dTilt, rate, stepS, capS);
    const byRate = flyTiltedCapture(gm, r0, vr0, vt0, accelMps2, targetEnergy,
      tilt, rate + dRate, stepS, capS);
    if (byTilt === null || byRate === null) return null;
    const j11 = (byTilt.radiusMiss - cut.radiusMiss) / dTilt;
    const j21 = (byTilt.radialMiss - cut.radialMiss) / dTilt;
    const j12 = (byRate.radiusMiss - cut.radiusMiss) / dRate;
    const j22 = (byRate.radialMiss - cut.radialMiss) / dRate;
    const det = j11 * j22 - j12 * j21;
    if (!(Math.abs(det) > 0)) return null;
    let stepTilt = -(j22 * cut.radiusMiss - j12 * cut.radialMiss) / det;
    let stepRate = -(-j21 * cut.radiusMiss + j11 * cut.radialMiss) / det;
    // Newton on a burn this long is linear only over a few hundredths of a radian.
    const reach = Math.max(Math.abs(stepTilt), Math.abs(stepRate) * cut.burnS);
    if (reach > 0.05) {
      stepTilt *= 0.05 / reach;
      stepRate *= 0.05 / reach;
    }
    tilt += stepTilt;
    rate += stepRate;
    cut = flyTiltedCapture(gm, r0, vr0, vt0, accelMps2, targetEnergy, tilt, rate, stepS, capS);
  }
  if (cut === null) return null;
  if (Math.abs(tilt) > CAPTURE_GUIDE_MAX_RAD || Math.abs(tilt + rate * cut.burnS) > CAPTURE_GUIDE_MAX_RAD) {
    return null;
  }
  return { tiltRad: tilt, rateRadS: rate, burnS: cut.burnS };
}

/** Of the park's height above a moon's surface, how far along the moon's track
 *  the ship a match leaves beside it moves away from the moon each turn. */
export const PARK_DRIFT_FRACTION = 0.5;

/**
 * How far from a moon's centre, in its radii, a velocity match's approach
 * passes if its brake is never flown, held out of the moon's orbital plane
 * alone. Beside the moon the parent's pull leaves the offset out of the plane
 * where the in-plane brake and the in-plane swing put nothing, so a brake cut
 * short keeps it; the moon's own pull does bend it, and the offset is sized
 * as the miss whose hyperbola about the moon bottoms out here at the approach
 * speed (metres more at Phobos and Deimos, half as far again at Mimas at
 * 100 m/s).
 * One radius over the surface clears Phobos's 1.17-radius long axis.
 */
const MOON_PASS_RADII = 2;

/** Simulated seconds between the moon states `matchBrakeStartS` is flown
 *  against; the track between them is the cubic through both ends. */
const MATCH_TRACK_STEP_S = 600;

/** What the trip's dated steps are set from. */
export type AutopilotScheduleConfig = Pick<AutopilotConfig, 'accelMps2' | 'arrivalSizedForAu' | 'capturePricedPush'>;

/** The dated steps a plan is flown on, as they stand before the flight times
 *  the brake off the approach the ship is really on. */
interface AutopilotSchedule {
  readonly departStartJd: number;
  readonly plannedLeadS: number;
  readonly brakeJd: number;
  readonly trimJds: readonly number[];
  /** Per rung, the seconds ahead of the brake it is counted back from; null on
   *  a rung counted from the arrival epoch. */
  readonly trimFromBrakeS: readonly (number | null)[];
}

function scheduleOf(plan: TransferPlan, config: AutopilotScheduleConfig): AutopilotSchedule {
  const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
  const capture = plan.burns.find((b) => b.kind === 'capture') ?? null;
  const accel = config.accelMps2;
  const sizedForAu = config.arrivalSizedForAu === true;
  const pricedPush = config.capturePricedPush === true;
  const finite = pricedPush ? null : capture?.finiteCapture ?? null;
  const profile = capture?.frame === 'body-relative' ? profileCaptureOf(finite, accel) : null;
  // Where the capture's throttle is planned to open: far enough ahead of the
  // periapsis for the finite burn to end on the parking orbit, or half an
  // instant-priced burn ahead of the epoch so it straddles the point the plan
  // names.
  const plannedLeadS = capture === null || pricedPush || !(accel > 0) ? 0
    : profile !== null ? profileLeadS(capture.vInfKmS * 1000, profile.circularMps, accel)
      : finite !== null ? finite.leadS
        : (capture.dvKmS * 1000) / (2 * accel);
  const brakeJd = plan.arrivalEpochJd - plannedLeadS / SECONDS_PER_DAY;
  const tofS = plan.timeOfFlightDays * SECONDS_PER_DAY;
  // The instant push, not what the ship spends: this half-burn is what the
  // departure is centred on so the push straddles the instant the conic was
  // solved for. The climb the finite figure carries is paid after the ship has
  // left the point the plan named, and charging the centring for it starts the
  // burn a third of an orbit early, which leaves along another asymptote.
  const departSeconds = departure === null ? 0 : (departure.impulseDvKmS * 1000) / accel;
  const departStartJd = departure === null
    ? plan.departureEpochJd
    : departure.epochJd - (departSeconds * 0.5) / SECONDS_PER_DAY;
  // Six hours before arrival is before the departure burn is over on a flight to
  // a moon, and a rung the trip cannot hold lands out of order in a ladder that
  // is walked in list order. Each absolute rung falls back on the ladder's own
  // tail there and takes that tail rung's place, still counted from the brake.
  const tailFrom = TRIM_TGO_FRACTIONS.length - FINAL_TRIM_TGO_S.length;
  const tail = TRIM_TGO_FRACTIONS.slice(tailFrom);
  const finalTrimS = sizedForAu
    ? FINAL_TRIM_TGO_S
    : FINAL_TRIM_TGO_S.map((s, i) => Math.min(s, tail[i]! * tofS));
  // The last rungs are counted back from the brake, which on a finite capture
  // opens an hour or more before the epoch the rest are counted back from. A
  // tail rung kept counted from the epoch instead can land inside the brake's
  // own slew, or past its start where it is never flown.
  const rungs = TRIM_TGO_FRACTIONS
    .filter((_, i) => i < tailFrom || finalTrimS[i - tailFrom] === FINAL_TRIM_TGO_S[i - tailFrom])
    .map((f) => ({ jd: plan.arrivalEpochJd - (f * tofS) / SECONDS_PER_DAY, fromBrakeS: null as number | null }))
    .concat(finalTrimS.map((s) => ({ jd: brakeJd - s / SECONDS_PER_DAY, fromBrakeS: s })))
    .filter((r) => r.jd > departStartJd)
    // A fallback's replacement is appended after the arrival-counted rungs,
    // not necessarily after them in time; sort before the strict-increase
    // pass so it drops against its chronological neighbour, not its array one.
    .sort((a, b) => a.jd - b.jd)
    .filter((r, i, all) => i === 0 || r.jd > all[i - 1]!.jd);
  return {
    departStartJd, plannedLeadS, brakeJd,
    trimJds: rungs.map((r) => r.jd),
    trimFromBrakeS: rungs.map((r) => r.fromBrakeS),
  };
}

/** The ship a departure's start is timed from, heliocentric. */
export interface DepartureTimingInput {
  readonly jd: number;
  readonly at: FlightVec;
  readonly velocity: FlightVec;
  /** As `AutopilotInput.burnThrustShare`. */
  readonly burnThrustShare?: number;
}

/** The body a plan's departure climbs out of. */
export function departureOriginId(plan: TransferPlan): string {
  const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
  return departure?.localBurn?.referenceId ?? departure?.referenceId ?? 'sol';
}

/** Where the schedule centres the departure's push, before any timing. */
export function scheduledDepartureStartJd(plan: TransferPlan, accelMps2: number): number {
  return scheduleOf(plan, { accelMps2 }).departStartJd;
}

/** Whether GO times this plan's departure off the ship's own orbit: an escape
 *  on a finite burn. One that stays inside the well opens where it is scheduled. */
export function departureIsTimed(plan: TransferPlan, accelMps2: number, escapeDeparture = false): boolean {
  const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
  return departure !== null && departure.localBurn !== null && accelMps2 > 0
    && (departure.boundSemiMajorKm === null || escapeDeparture);
}

/** Seconds ahead of the scheduled start the timing is made at; 0 off a closed orbit. */
export function departureTimingReachS(periodS: number): number {
  return Number.isFinite(periodS) ? Math.max(periodS, DEPARTURE_TIMING_REACH_S) : 0;
}

const periodOriginAt = vec();
const periodOriginVel = vec();

/** Period of the ship's own orbit about `originId`, seconds; NaN off a closed one. */
export function departureOrbitPeriodS(
  env: AutopilotEnv, originId: string, jd: number, at: FlightVec, velocity: FlightVec,
): number {
  const gm = env.gmOf(originId);
  env.bodyAt(originId, jd, periodOriginAt);
  env.bodyVelocityAt(originId, jd, periodOriginVel);
  const r = Math.hypot(at.x - periodOriginAt.x, at.y - periodOriginAt.y, at.z - periodOriginAt.z);
  const v = Math.hypot(
    velocity.x - periodOriginVel.x, velocity.y - periodOriginVel.y, velocity.z - periodOriginVel.z);
  if (!(gm > 0) || !(r > 0)) return Number.NaN;
  const inverseA = 2 / r - (v * v) / gm;
  if (!(inverseA > 0)) return Number.NaN;
  return TWO_PI * Math.sqrt(1 / (inverseA * inverseA * inverseA * gm));
}

/**
 * When the departure opens, so that the prograde climb from the ship's own
 * orbit leaves along the plan's asymptote as near as that orbit's plane
 * allows. The climb is the one `finiteEscapeEnd` integrates, from where the
 * ship coasts to by each trial start; its exit turns with the start, so each
 * round moves the start by the angle still missing over the rate it turns
 * at. A reason where the climb alone does not fit under the departure's
 * ceiling at the timed start; a swing after it that does not fit is left to
 * the course fixes. Where no start can be timed near `scheduledStartJd`, that,
 * as an untimed departure flies. GO and the DEPARTURE row both time off this.
 */
export function timedDepartureStartJd(
  plan: TransferPlan, env: AutopilotEnv, accelMps2: number, input: DepartureTimingInput,
  periodS: number, leadS: number, scheduledStartJd: number,
): number | string {
  const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
  if (departure === null) return scheduledStartJd;
  const originId = departureOriginId(plan);
  const asymptote = departure.direction;
  const share = input.burnThrustShare;
  const thrustShare = share !== undefined && share > 0 && share < 1 ? share : 1;
  const tofS = plan.timeOfFlightDays * SECONDS_PER_DAY;
  const gm = env.gmOf(originId);
  const vInf = departure.vInfKmS * 1000;
  const originAt = vec();
  const originVel = vec();
  env.bodyAt(originId, input.jd, originAt);
  env.bodyVelocityAt(originId, input.jd, originVel);
  const r0 = {
    x: input.at.x - originAt.x, y: input.at.y - originAt.y, z: input.at.z - originAt.z,
  };
  const v0 = {
    x: input.velocity.x - originVel.x,
    y: input.velocity.y - originVel.y,
    z: input.velocity.z - originVel.z,
  };
  const hx = r0.y * v0.z - r0.z * v0.y;
  const hy = r0.z * v0.x - r0.x * v0.z;
  const hz = r0.x * v0.y - r0.y * v0.x;
  const hl = Math.hypot(hx, hy, hz);
  // The plan's asymptote laid into the ship's plane: the prograde climb
  // never leaves that plane, and the swing after it buys the rest.
  const along = hl > 0 ? (asymptote.x * hx + asymptote.y * hy + asymptote.z * hz) / (hl * hl) : 0;
  const tx = asymptote.x - along * hx;
  const ty = asymptote.y - along * hy;
  const tz = asymptote.z - along * hz;
  const inPlane = hl > 0 && Math.hypot(tx, ty, tz) > 1e-9;
  const at = vec();
  const vel = vec();
  const exit = vec();
  const endRel = vec();
  const endVel = vec();
  const climbFrom = (tS: number): number | null => {
    if (!keplerAfter(gm, r0, v0, tS, at, vel)) return null;
    const end = finiteEscapeEnd(gm / 1e9,
      { x: at.x / 1000, y: at.y / 1000, z: at.z / 1000 },
      { x: vel.x / 1000, y: vel.y / 1000, z: vel.z / 1000 },
      (accelMps2 * thrustShare) / 1000, vInf / 1000, periodS * DEPARTURE_TIMING_STEP_FRACTION);
    if (end === null) return null;
    endRel.x = end.positionKm.x * 1000;
    endRel.y = end.positionKm.y * 1000;
    endRel.z = end.positionKm.z * 1000;
    endVel.x = end.velocityKmS.x * 1000;
    endVel.y = end.velocityKmS.y * 1000;
    endVel.z = end.velocityKmS.z * 1000;
    return end.dvKmS * 1000;
  };
  // Signed angle from the climb's exit round to the aim, about the pole.
  const missRad = (): number => {
    if (!inPlane || !outgoingAsymptote(endRel, endVel, gm, exit)) return 0;
    const cx = exit.y * tz - exit.z * ty;
    const cy = exit.z * tx - exit.x * tz;
    const cz = exit.x * ty - exit.y * tx;
    return Math.atan2((cx * hx + cy * hy + cz * hz) / hl, exit.x * tx + exit.y * ty + exit.z * tz);
  };
  const rate = TWO_PI / periodS;
  let tS = (scheduledStartJd - input.jd) * SECONDS_PER_DAY;
  let climbMps = climbFrom(tS);
  if (climbMps === null) return scheduledStartJd;
  let miss = missRad();
  const scheduledS = tS;
  let lastT = tS;
  let lastMiss = miss;
  let bestT = tS;
  let bestMiss = miss;
  let bestClimbMps = climbMps;
  for (let i = 0; i < DEPARTURE_TIMING_ROUNDS && Math.abs(miss) > DEPARTURE_TIMING_TOL_RAD; i++) {
    const slope = i > 0 && tS !== lastT ? (miss - lastMiss) / (tS - lastT) : -rate;
    lastT = tS;
    lastMiss = miss;
    // A secant across the miss's wrap at half a turn reads a slope near
    // zero, so no round moves the start more than a quarter revolution.
    const stepS = -miss / (slope < 0 ? slope : -rate);
    tS += Math.max(-periodS / 4, Math.min(periodS / 4, stepS));
    const next = climbFrom(tS);
    if (next === null) {
      tS = lastT;
      climbFrom(tS);
      break;
    }
    climbMps = next;
    miss = missRad();
    if (Math.abs(miss) < Math.abs(bestMiss)) {
      bestT = tS;
      bestMiss = miss;
      bestClimbMps = climbMps;
    }
  }
  tS = bestT;
  climbMps = bestClimbMps;
  // The coast is periodic, so a start a revolution on climbs the same way:
  // the first at or after the schedule's, which a skip aimed at it cannot pass.
  tS -= Math.floor((tS - scheduledS) / periodS) * periodS;
  while (tS < leadS) tS += periodS;
  if (tS - scheduledS > DEPARTURE_TIMING_SLIP_FRACTION * tofS) return scheduledStartJd;
  const ceiling = departure.impulseDvKmS * 1000 * DEPARTURE_CEILING_FACTOR;
  if (climbMps > ceiling) {
    return `leaving this orbit toward ${plan.destinationName} would cost ${(climbMps / 1000).toFixed(1)} km/s, past the ${(ceiling / 1000).toFixed(1)} km/s the departure can carry`;
  }
  return input.jd + tS / SECONDS_PER_DAY;
}

/** One step of a trip as GO flies it. `dvMps` is null where the step spends
 *  nothing, and on a course fix, whose push is solved on the day from where
 *  the ship really is. */
export interface AutopilotPlanStep {
  readonly kind: 'wait' | 'depart' | 'coast' | 'fix' | 'capture' | 'arrive';
  readonly startJd: number;
  readonly endJd: number;
  readonly dvMps: number | null;
}

/** Every step `createAutopilot` flies a plan through, in order, off its own
 *  schedule. A rung due mid-push is flown as the push ends, and one not due
 *  before the brake is never flown, the way `step` walks them. */
export function autopilotFlightPlan(plan: TransferPlan, config: AutopilotScheduleConfig): AutopilotPlanStep[] {
  const accel = config.accelMps2;
  if (!(accel > 0)) return [];
  const { departStartJd, brakeJd, trimJds } = scheduleOf(plan, config);
  const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
  const capture = plan.burns.find((b) => b.kind === 'capture') ?? null;
  const burnDays = (dvKmS: number): number => (dvKmS * 1000) / accel / SECONDS_PER_DAY;
  const steps: AutopilotPlanStep[] = [];
  let at = plan.departureEpochJd - plan.windowWaitDays;
  const hold = (kind: 'wait' | 'coast', untilJd: number): void => {
    if (!(untilJd > at)) return;
    steps.push({ kind, startJd: at, endJd: untilJd, dvMps: null });
    at = untilJd;
  };
  const push = (kind: 'depart' | 'capture', burn: TransferBurn): void => {
    const end = at + burnDays(burn.dvKmS);
    steps.push({ kind, startJd: at, endJd: end, dvMps: burn.dvKmS * 1000 });
    at = end;
  };
  hold('wait', departStartJd);
  if (departure !== null) push('depart', departure);
  for (const jd of trimJds) {
    if (!(jd < brakeJd)) break;
    hold('coast', jd);
    steps.push({ kind: 'fix', startJd: at, endJd: at, dvMps: null });
  }
  hold('coast', brakeJd);
  if (capture !== null) push('capture', capture);
  steps.push({ kind: 'arrive', startJd: at, endJd: at, dvMps: null });
  return steps;
}

export function createAutopilot(
  plan: TransferPlan, config: AutopilotConfig, env: AutopilotEnv,
): Autopilot {
  const departure = plan.burns.find((b) => b.kind === 'departure') ?? null;
  const capture = plan.burns.find((b) => b.kind === 'capture') ?? null;
  const originId = departure?.localBurn?.referenceId ?? departure?.referenceId ?? 'sol';
  const destinationId = plan.destinationId;
  const accel = config.accelMps2;
  const fps = config.framesPerSecond ?? DEFAULT_FPS;
  const sizedForAu = config.arrivalSizedForAu === true;
  const parkingM = config.parkingAltitudeM ?? plannedParkingAltitudeM(plan);
  const standoff = config.arrivalStandoffM
    ?? (sizedForAu ? parkingM
      : config.aimThroughCentre === true
        ? centreStandoffFor(env.radiusOf(destinationId), parkingM)
        : arrivalStandoffFor(env.radiusOf(destinationId), parkingM));
  // A capture into orbit is priced at a periapsis; a velocity match at a body
  // too small to hold an orbit has none, and stops short on the approach.
  const aimsAtPeriapsis = capture !== null && capture.frame === 'body-relative'
    && config.aimOnApproachLine !== true;
  const pricedPush = config.capturePricedPush === true;
  const finite = pricedPush ? null : capture?.finiteCapture ?? null;
  // A control that moves the aim keeps moving it; otherwise a capture priced
  // as a finite burn is aimed at the periapsis that burn is priced from.
  const aimsAtFinite = finite !== null && config.arrivalStandoffM === undefined && !sizedForAu
    && config.aimThroughCentre !== true;
  const aimPeriapsisM = aimsAtFinite ? captureAimPeriapsisM(capture, standoff) : standoff;
  const parkingRadiusM = finite === null ? standoff : finite.parkingRadiusKm * 1000;
  const schedule = scheduleOf(plan, config);
  const plannedLeadS = schedule.plannedLeadS;
  let brakeJd = schedule.brakeJd;
  let brakeTimed = false;
  const captureLaw: 'orbit' | 'match' | null = capture === null || pricedPush ? null
    : capture.frame === 'body-relative' ? 'orbit' : 'match';
  const profile = captureLaw === 'orbit' ? profileCaptureOf(finite, accel) : null;
  // The guided capture's tilt, as solved at `tiltJd`; null until its throttle opens.
  let captureTilt: CaptureTilt | null = null;
  let tiltJd = 0;
  const trimCeiling = config.trimCeilingMps ?? DEFAULT_TRIM_CEILING_MPS;
  const trimBudget = config.trimBudgetMps ?? DEFAULT_TRIM_BUDGET_MPS;
  const solveBudget = config.solveStepBudget ?? SOLVE_STEP_BUDGET;
  const solveMsBudget = config.solveMsBudget ?? SOLVE_MS_BUDGET;
  const rates = config.warpRates.length > 0 ? config.warpRates : [1];
  const modelIds = config.modelIds ?? dedupe(['sol', originId, destinationId]);

  const tofS = plan.timeOfFlightDays * SECONDS_PER_DAY;
  let departStartJd = schedule.departStartJd;
  // The start is timed off the ship's own orbit once, DEPARTURE_TIMING_REACH_S
  // or a revolution out, where a prediction off it has not had months to drift.
  let departTimed = !departureIsTimed(plan, accel, config.escapeDeparture === true)
    || config.untimedDeparture === true
    || config.progradeDeparture === true;
  let waitTurnLeadS = 0;
  let waitTimingReachS = DEPARTURE_TIMING_REACH_S;
  // Sim seconds one nose turn may cost, which is what keeps a slew at warp from
  // eating a short flight. Unbounded under the control.
  const slewBudgetS = sizedForAu ? Infinity : SLEW_TRIP_FRACTION * tofS;
  const leftFraction = sizedForAu ? Infinity : SLEW_LEFT_FRACTION;
  const maxFrameS = sizedForAu ? Infinity : FRAME_TRIP_FRACTION * tofS;
  const trimJds = schedule.trimJds.slice();
  let plannedDriveMps2 = accel;
  // Where an energy-law capture on that drive opens from; null at full thrust.
  let aimForDriveM: number | null = null;

  let phase: AutopilotPhase = 'wait';
  let ended: AutopilotEnd | null = null;
  let burn: BurnOrder | null = null;
  let trimIdx = 0;
  let trimSpent = 0;
  let flownSpent = 0;
  let solve: GuidanceSolve | null = null;
  let held: AutopilotCommand | null = null;
  // A rung seen due once. The solve opens on the NEXT update, which is the first
  // the caller has been told to freeze, so it is seeded from a ship standing still.
  let trimArmed = false;
  /** The next rung's solve opened ahead of its date off the coast predicted to it,
   *  and the last such solve landed for that rung. */
  let ahead: AheadSolve | null = null;
  let aheadLanded: AheadSolve | null = null;
  /** Metres between where the landed solve predicted the ship at its rung and
   *  where it was, read at the rung. */
  let aheadOffM = Number.NaN;
  /** The burn in hand's split ceiling before the ladder rounds it down. */
  let burnSpanRate = 0;
  /** performance.now() at the top of the update in flight. */
  let callStartMs = 0;
  /** The finite capture's climbs, flown once and read by every re-pricing after. */
  const climbs = config.sliceOwnBudget === true ? undefined : createFiniteCaptureMemo();
  let climbsWarm = climbs === undefined;

  /** The plan's own asymptote, unit, in the axes everything here is in. */
  const asymptote = vec();
  if (departure !== null) {
    asymptote.x = departure.direction.x;
    asymptote.y = departure.direction.y;
    asymptote.z = departure.direction.z;
  }

  const axes: FlightAxes = { pitch: 0, yaw: 0, roll: 0 };
  const noseTarget = vec();
  const originAt = vec();
  const originVel = vec();
  const destAt = vec();
  const aimOffset = vec();
  const destVel = vec();
  const rel = vec();
  const relVel = vec();
  const arriveAt = vec();
  const originThen = vec();
  let heldGoMps = Number.NaN;
  let heldSpentMps = 0;
  const aim = vec();
  const required = vec();
  const coOrbit = vec();
  const moonRel = vec();
  const moonRelVel = vec();
  const shipRel = vec();
  const shipRelVel = vec();
  const parkAt = vec();
  const matchParentId = matchParentIdFor(departure, originId);
  const model = createModel(env, modelIds);

  /** The destination's place and velocity about `matchParentId` at `jd`. */
  function moonState(jd: number, at: FlightVec, vel: FlightVec): void {
    env.bodyAt(destinationId, jd, destAt);
    env.bodyVelocityAt(destinationId, jd, destVel);
    env.bodyAt(matchParentId, jd, originAt);
    env.bodyVelocityAt(matchParentId, jd, originVel);
    at.x = destAt.x - originAt.x;
    at.y = destAt.y - originAt.y;
    at.z = destAt.z - originAt.z;
    vel.x = destVel.x - originVel.x;
    vel.y = destVel.y - originVel.y;
    vel.z = destVel.z - originVel.z;
  }

  // A match parks the ship beside the moon on an orbit whose period carries it
  // further from the moon every turn (`matchParkFor`).
  const heightM = standoff - env.radiusOf(destinationId);
  const park: MatchPark | null = captureLaw === 'match' && capture !== null
    ? matchParkFor(env.gmOf(matchParentId), moonState, plan.arrivalEpochJd, capture.direction,
      standoff, PARK_DRIFT_FRACTION * (heightM > 0 ? heightM : standoff),
      config.parkOnMoonEnergy === true)
    : null;
  // The park is aimed at where it stands when the match is due to be over, the
  // guidance's arrival epoch plus the half of it that follows.
  const parkRestJd = plan.arrivalEpochJd + plannedLeadS / SECONDS_PER_DAY;

  // The side of the moon's orbital plane a match's approach passes on, and how
  // far from the centre (`MOON_PASS_RADII`).
  const moonPole = vec();
  const moonCentre = vec();
  const passSide = vec();
  let passM = 0;
  // Which side of the plane, held from the first move on: +1 the pole's, -1
  // the other, 0 not chosen yet.
  let passSign = 0;
  if (park !== null && config.aimThroughMoon !== true) {
    moonState(plan.arrivalEpochJd, moonRel, moonRelVel);
    moonPole.x = moonRel.y * moonRelVel.z - moonRel.z * moonRelVel.y;
    moonPole.y = moonRel.z * moonRelVel.x - moonRel.x * moonRelVel.z;
    moonPole.z = moonRel.x * moonRelVel.y - moonRel.y * moonRelVel.x;
    const l = norm(moonPole);
    if (l > 0) {
      moonPole.x /= l;
      moonPole.y /= l;
      moonPole.z /= l;
      passM = MOON_PASS_RADII * env.radiusOf(destinationId);
    }
  }

  /** Where a match's aim `out` sits out of the moon's plane, along its pole
   *  taken across the unit `approach`, closer than the miss that passes the
   *  moon at `passM` at `speedMps`, move it out to that miss, on the side of
   *  the plane the first such move found it on. The part in the plane counts
   *  for nothing: it is what the brake sweeps. The trims make this move and
   *  the departure does not: across its half-turn arc one out of the plane is
   *  dearest. */
  function clearMoon(out: FlightVec, approach: FlightVec, speedMps: number): void {
    if (!(passM > 0) || !(speedMps > 0)) return;
    const along = moonPole.x * approach.x + moonPole.y * approach.y + moonPole.z * approach.z;
    passSide.x = moonPole.x - along * approach.x;
    passSide.y = moonPole.y - along * approach.y;
    passSide.z = moonPole.z - along * approach.z;
    const l = norm(passSide);
    if (!(l > 0)) return;
    const missM = Math.sqrt(
      passM * passM + (2 * env.gmOf(destinationId) * passM) / (speedMps * speedMps));
    env.bodyAt(destinationId, plan.arrivalEpochJd, moonCentre);
    const h = ((out.x - moonCentre.x) * passSide.x + (out.y - moonCentre.y) * passSide.y
      + (out.z - moonCentre.z) * passSide.z) / l;
    if (Math.abs(h) >= missM) return;
    if (passSign === 0) passSign = h < 0 ? -1 : 1;
    const lift = (passSign * missM - h) / l;
    out.x += lift * passSide.x;
    out.y += lift * passSide.y;
    out.z += lift * passSide.z;
  }

  /** The park `jd` puts beside the moon, from the moon's centre, into `out`. */
  function parkOffset(jd: number, out: FlightVec): boolean {
    if (park === null) return false;
    moonState(jd, moonRel, moonRelVel);
    if (!parkPointFor(moonRel, moonRelVel, park, out)) return false;
    out.x -= moonRel.x;
    out.y -= moonRel.y;
    out.z -= moonRel.z;
    return true;
  }

  /** The moon about `matchParentId` from `jd0` on, as `matchBrakeStartS` reads
   *  it: states `MATCH_TRACK_STEP_S` apart, and the cubic through both ends of
   *  the span between them. */
  function moonTrackFrom(jd0: number): MoonTrack {
    const states = new Map<number, { at: FlightVec; vel: FlightVec }>();
    const stateAt = (k: number): { at: FlightVec; vel: FlightVec } => {
      let s = states.get(k);
      if (s === undefined) {
        s = { at: vec(), vel: vec() };
        moonState(jd0 + (k * MATCH_TRACK_STEP_S) / SECONDS_PER_DAY, s.at, s.vel);
        states.set(k, s);
      }
      return s;
    };
    const h = MATCH_TRACK_STEP_S;
    return (tS, at, vel) => {
      const k = Math.max(0, Math.floor(tS / h));
      const s0 = stateAt(k);
      const s1 = stateAt(k + 1);
      const s = tS / h - k;
      const s2 = s * s;
      const s3 = s2 * s;
      const a0 = 2 * s3 - 3 * s2 + 1;
      const b0 = (s3 - 2 * s2 + s) * h;
      const a1 = -2 * s3 + 3 * s2;
      const b1 = (s3 - s2) * h;
      const da0 = (6 * s2 - 6 * s) / h;
      const db0 = 3 * s2 - 4 * s + 1;
      const da1 = (-6 * s2 + 6 * s) / h;
      const db1 = 3 * s2 - 2 * s;
      at.x = a0 * s0.at.x + b0 * s0.vel.x + a1 * s1.at.x + b1 * s1.vel.x;
      at.y = a0 * s0.at.y + b0 * s0.vel.y + a1 * s1.at.y + b1 * s1.vel.y;
      at.z = a0 * s0.at.z + b0 * s0.vel.z + a1 * s1.at.z + b1 * s1.vel.z;
      vel.x = da0 * s0.at.x + db0 * s0.vel.x + da1 * s1.at.x + db1 * s1.vel.x;
      vel.y = da0 * s0.at.y + db0 * s0.vel.y + da1 * s1.at.y + db1 * s1.vel.y;
      vel.z = da0 * s0.at.z + db0 * s0.vel.z + da1 * s1.at.z + db1 * s1.vel.z;
    };
  }

  function command(
    throttle: number, warpRate: number, target: FlightVec | null, errorRad: number,
    nextEventS: number, burnStepS = 0, ceiling = warpRate,
  ): AutopilotCommand {
    return {
      phase,
      axes,
      throttle,
      warpRate,
      warpCeiling: ceiling,
      burnStepS,
      noseTarget: target,
      noseErrorRad: errorRad,
      dvToGoMps: burn === null ? 0 : burn.dvToGoMps,
      nextEventS,
      nextStep: stepAhead(),
      trimSpentMps: trimSpent,
      spentMps: flownSpent,
      solving: solve !== null,
      fixSolved: phase === 'coast' && aheadLanded !== null && aheadLanded.idx === trimIdx,
      burnSpanRate: burnStepS > 0 ? burnSpanRate : ceiling,
      departureUntimed: phase === 'wait' && !departTimed,
      turnLeadS: phase === 'wait' ? waitTurnLeadS : 0,
      timingReachS: phase === 'wait' && !departTimed ? waitTimingReachS : undefined,
      done: ended !== null,
      ended,
    };
  }

  /** The last command while a solve finishes, at the bottom warp rung: the ship
   *  must not move while the solve believes it is where it started. */
  function holdForSolve(): AutopilotCommand {
    if (held === null) return idle(rates[0]!, 0);
    return {
      ...held, warpRate: rates[0]!, warpCeiling: rates[0]!, burnStepS: 0, solving: true,
    };
  }

  function idle(warpRate: number, nextEventS: number): AutopilotCommand {
    axes.pitch = 0;
    axes.yaw = 0;
    axes.roll = 0;
    return command(0, warpRate, null, 0, nextEventS);
  }

  function finish(next: 'arrived' | 'handback' | 'refused', reason: string): AutopilotCommand {
    phase = next;
    burn = null;
    solve = null;
    ended = { phase: next, reason };
    return idle(rates[0]!, 0);
  }

  // The largest warp that still leaves WARP_LEAD_REAL_S real seconds before the
  // event, floored at the burn's rate and capped so no frame outlasts the wait.
  function warpForWait(secondsToEvent: number, floor: number): number {
    let best = rates[0]!;
    let capped = rates[0]!;
    for (const rate of rates) {
      if (rate / fps > maxFrameS) continue;
      if (rate * WARP_LEAD_REAL_S <= secondsToEvent) best = Math.max(best, rate);
      if (rate / fps <= secondsToEvent) capped = Math.max(capped, rate);
    }
    // The floor may not buy a frame LONGER than the wait it is in: at 1 DAY/S a
    // rung fires a quarter of an hour late, off a state 43,000 km from its own.
    return Math.min(Math.max(best, floor), capped);
  }

  // Simulated seconds one guidance step of a burn may cover: it spends at most
  // BURN_DV_PER_FRAME_MPS and lasts a fraction of the local orbital period.
  function burnStepSeconds(at: FlightVec, jd: number): number {
    let shortest = Infinity;
    for (const id of modelIds) {
      const gm = env.gmOf(id);
      if (!(gm > 0)) continue;
      env.bodyAt(id, jd, _probe);
      const r = Math.hypot(_probe.x - at.x, _probe.y - at.y, _probe.z - at.z);
      if (!(r > 0)) continue;
      const period = 2 * Math.PI * Math.sqrt((r * r * r) / gm);
      const cap = period * BURN_FRAME_PERIOD_FRACTION;
      if (cap < shortest) shortest = cap;
    }
    const dvStep = accel > 0 ? BURN_DV_PER_FRAME_MPS / accel : Infinity;
    return Math.min(shortest, dvStep, maxFrameS);
  }

  /** The largest warp a span of `spanS` simulated seconds a frame carries. */
  function warpForSpan(spanS: number): number {
    let best = rates[0]!;
    for (const rate of rates) {
      if (rate / fps <= spanS) best = Math.max(best, rate);
    }
    return best;
  }

  // The largest warp a burn may run at with ONE guidance step a frame. The
  // lead a slew or a pre-start wait is priced at, which is real seconds of the
  // clock this rate and not the split one below: nothing splits a frame that
  // is not spending.
  function warpForBurn(at: FlightVec, jd: number): number {
    return warpForSpan(burnStepSeconds(at, jd));
  }

  // The warp a nose turn may run at. A frame gives the turn its own real
  // seconds and the clock whatever the rate is, so the sim seconds a slew costs
  // are the rate times the real seconds it has left to turn: capped on the trip,
  // never above the rate the burn itself would run at.
  function warpForSlew(jd: number, errorRad: number, burnRate: number): number {
    const slewRealS = errorRad / config.turnRateRadS;
    if (!(slewRealS > 0)) return burnRate;
    const cap = slewBudgetAt(jd) / slewRealS;
    let best = rates[0]!;
    for (const rate of rates) {
      if (rate <= cap && rate <= burnRate) best = Math.max(best, rate);
    }
    return best;
  }

  /**
   * Sim seconds a nose turn MID-TRIP may cost: a slice of the trip, and never
   * more than a slice of what is left of it.
   *
   * Zero once the arrival epoch is past, where there is nothing left to charge
   * a turn against. A capture is hours of engine opening at its own epoch, so
   * most of its turns happen on the far side of that instant, and falling back
   * to the whole trip's budget there would hand the endgame the loosest number
   * in the rule instead of the tightest: a zero budget puts those turns at real
   * time, which is what an hour from the destination is worth. It also keeps
   * `--inject slew-on-trip-budget` telling apart from the normal path
   * everywhere, rather than agreeing with it past the epoch.
   */
  function slewBudgetAt(jd: number): number {
    if (config.slewOnTripBudget === true || leftFraction === Infinity) return slewBudgetS;
    const leftS = (plan.arrivalEpochJd - jd) * SECONDS_PER_DAY;
    return Math.min(slewBudgetS, leftFraction * Math.max(0, leftS));
  }

  /**
   * Sim seconds of slew a dated burn opens before its own start epoch: the real
   * seconds a turn takes, at the warp the burn itself would run at, under the
   * trip-wide budget so a lead cannot eat a short flight.
   *
   * Not under `slewBudgetAt`. That budget is a hundredth of the time still to
   * run to an epoch, and a lead measured back from that same epoch cannot be
   * longer than a hundredth of itself: the capture's window then opens only
   * once its own epoch is past, on every trip and every pair, with the nose
   * still wherever the coast left it. `--inject capture-opens-at-epoch` puts
   * that cap back, measured to the brake's own epoch.
   */
  function alignLeadS(input: AutopilotInput): number {
    const budget = config.captureOpensAtEpoch !== true || leftFraction === Infinity
      ? slewBudgetS
      : Math.min(slewBudgetS,
        leftFraction * Math.max(0, (brakeJd - input.jd) * SECONDS_PER_DAY));
    return Math.min(ALIGN_LEAD_REAL_S * warpForBurn(input.at, input.jd), budget);
  }

  /** The aim: the periapsis `periapsisAimFor` names where the arrival captures
   *  into orbit, the park beside the moon where it matches the moon's orbit,
   *  and the destination pulled back along the approach by the standoff where
   *  the capture is only the priced push. */
  function aimPoint(jd: number, at: FlightVec, velocity: FlightVec, out: FlightVec): void {
    env.bodyAt(destinationId, plan.arrivalEpochJd, out);
    if (!(standoff > 0)) return;
    if (captureLaw === 'match' && parkOffset(parkRestJd, parkAt)) {
      out.x += parkAt.x;
      out.y += parkAt.y;
      out.z += parkAt.z;
      return;
    }
    env.bodyVelocityAt(destinationId, jd, destVel);
    if (aimsAtPeriapsis) {
      env.bodyAt(destinationId, jd, destAt);
      rel.x = at.x - destAt.x;
      rel.y = at.y - destAt.y;
      rel.z = at.z - destAt.z;
      relVel.x = velocity.x - destVel.x;
      relVel.y = velocity.y - destVel.y;
      relVel.z = velocity.z - destVel.z;
      const gm = env.gmOf(destinationId);
      // A profile capture flies its own pass, so only the energy law is re-aimed.
      const periapsisM = !aimsAtFinite ? aimPeriapsisM
        : aimForDriveM === null
          ? captureAimPeriapsisM(capture, standoff, { rel, relVel, gm, accelMps2: accel }, undefined, climbs)
          : captureAimPeriapsisM(capture, standoff, { rel, relVel, gm, accelMps2: plannedDriveMps2 },
            aimForDriveM, climbs);
      if (periapsisAimFor(rel, relVel, gm, periapsisM, (capture?.vInfKmS ?? 0) * 1000, aimOffset)) {
        out.x += aimOffset.x;
        out.y += aimOffset.y;
        out.z += aimOffset.z;
        return;
      }
    }
    relVel.x = velocity.x - destVel.x;
    relVel.y = velocity.y - destVel.y;
    relVel.z = velocity.z - destVel.z;
    const l = norm(relVel);
    if (!(l > 0)) return;
    out.x -= (relVel.x / l) * standoff;
    out.y -= (relVel.y / l) * standoff;
    out.z -= (relVel.z / l) * standoff;
  }

  /** The next trim rung is due at this epoch. */
  function trimDue(jd: number): boolean {
    return trimIdx < trimJds.length && jd >= trimJds[trimIdx]!;
  }

  function freezes(jd: number): boolean {
    if (ended !== null) return false;
    if (solve !== null) return true;
    return phase === 'coast' && trimArmed && trimDue(jd);
  }

  /** Where a trim aims: `aimPoint`, and on a match the part of it across the
   *  approach moved by what the match flown from here would miss the park by,
   *  since the brake carries the ship tens of kilometres off the coast's line,
   *  then lifted out of the moon's plane by `clearMoon`. Along the approach the
   *  brake's own timing puts the ship level with it. */
  function trimAim(input: AutopilotInput, out: FlightVec): void {
    aimPoint(input.jd, input.at, input.velocity, out);
    if (captureLaw !== 'match' || !(accel > 0)) return;
    const found = flyMatch(input);
    if (found === null) return;
    const u = found.approach;
    env.bodyAt(matchParentId, plan.arrivalEpochJd, originThen);
    const cx = originThen.x + found.coastEnd.x + found.miss.x - out.x;
    const cy = originThen.y + found.coastEnd.y + found.miss.y - out.y;
    const cz = originThen.z + found.coastEnd.z + found.miss.z - out.z;
    const along = cx * u.x + cy * u.y + cz * u.z;
    out.x += cx - along * u.x;
    out.y += cy - along * u.y;
    out.z += cz - along * u.z;
    clearMoon(out, u, found.approachMps);
  }

  /** Open the rung's solve toward `aimPoint` from here, or on a match toward the
   *  aim `trimAim` found the update before, whose flight is too dear to share an
   *  update with the solve's first slice. It is advanced a slice at a time from
   *  here on, and nothing else runs on the model until it lands. */
  function beginTrim(input: AutopilotInput): AutopilotCommand {
    const early = aheadLanded?.idx === trimIdx ? aheadLanded : ahead?.idx === trimIdx ? ahead : null;
    ahead = null;
    aheadLanded = null;
    if (early !== null) {
      aheadOffM = Math.hypot(input.at.x - early.at.x, input.at.y - early.at.y, input.at.z - early.at.z);
      solve = early.solve;
      return advanceSolve(input);
    }
    if (captureLaw !== 'match') aimPoint(input.jd, input.at, input.velocity, aim);
    solve = model.beginRequiredVelocity(
      input.at, input.velocity, input.jd, aim, plan.arrivalEpochJd);
    return advanceSolve(input);
  }

  /** The slice gets what the call has left of its budget, so the aim a rung's
   *  first slice shares a call with does not ride on top of a whole slice. */
  function sliceMsLeft(): number {
    return solveMsBudget > 0 && config.sliceOwnBudget !== true
      ? Math.max(SLICE_MIN_MS, solveMsBudget - (performance.now() - callStartMs))
      : solveMsBudget;
  }

  /**
   * One slice of the next rung's solve while the coast still runs: seeded off
   * the ship's coast predicted to the rung's date, and opened again from nearer
   * while the landed one was seeded more than AHEAD_REFRESH_S out, so what the
   * prediction misses stays that short. A match's aim is found off a flight
   * too dear to run beside it, so a match solves at the date.
   */
  function advanceAhead(input: AutopilotInput): void {
    if (captureLaw === 'match' || trimIdx >= trimJds.length || ended !== null) return;
    const dueJd = trimJds[trimIdx]!;
    if (ahead === null) {
      const landedOutS = aheadLanded?.idx === trimIdx ? (dueJd - aheadLanded.seedJd) * SECONDS_PER_DAY : Infinity;
      if (!(dueJd > input.jd) || landedOutS <= AHEAD_REFRESH_S) return;
      const seedAt = vec();
      ahead = {
        idx: trimIdx, seedJd: input.jd, at: seedAt,
        solve: model.beginRequiredVelocityAhead(input.at, input.velocity, input.jd, dueJd,
          (at, velocity, out) => aimPoint(dueJd, at, velocity, out), plan.arrivalEpochJd, seedAt,
          aheadLanded?.idx === trimIdx ? aheadLanded.solve.velocity : null),
      };
    }
    const status = ahead.solve.advance(solveBudget * AHEAD_STEP_FACTOR, sliceMsLeft());
    if (status === 'running') return;
    if (status === 'done') aheadLanded = ahead;
    ahead = null;
  }

  function advanceSolve(input: AutopilotInput): AutopilotCommand {
    const running = solve;
    if (running === null) return idle(rates[0]!, 0);
    const status = running.advance(solveBudget, sliceMsLeft());
    if (status === 'running') return holdForSolve();
    solve = null;
    if (status === 'failed') {
      return finish('refused',
        `the autopilot could not find a course to the arrival epoch from ${(norm(input.velocity)).toFixed(0)} m/s at JD ${input.jd.toFixed(3)}`);
    }
    if (running.velocityAt !== undefined) running.velocityAt(input.at, input.jd, required);
    else {
      required.x = running.velocity.x;
      required.y = running.velocity.y;
      required.z = running.velocity.z;
    }
    let dx = required.x - input.velocity.x;
    let dy = required.y - input.velocity.y;
    let dz = required.z - input.velocity.z;
    let dv = Math.hypot(dx, dy, dz);
    if (dv > trimCeiling) {
      const k = trimCeiling / dv;
      dx *= k;
      dy *= k;
      dz *= k;
      dv = trimCeiling;
    }
    if (trimSpent + dv > trimBudget) {
      return finish('refused',
        `the course correction wants ${dv.toFixed(0)} m/s on top of ${trimSpent.toFixed(0)} already trimmed, past the ${trimBudget.toFixed(0)} m/s this trip carries`);
    }
    if (dv <= TRIM_DEADBAND_MPS) {
      trimIdx++;
      phase = 'coast';
      const next = nextEventSeconds(input.jd);
      return idle(warpForWait(next, warpForBurn(input.at, input.jd)), next);
    }
    burn = {
      label: 'trim',
      steered: false,
      direction: { x: dx / dv, y: dy / dv, z: dz / dv },
      dvToGoMps: dv,
      targetEnergy: Number.NaN,
      targetVInf: 0,
      bound: false,
      boundNormal: null,
      pointing: false,
      startJd: input.jd,
      spentMps: 0,
      ceilingMps: dv,
      law: null,
    };
    phase = 'trim';
    return flyBurn(input);
  }

  /** The step `nextEventSeconds` is counting to. Only the two coasting phases
   *  count to a date; a burn's own count is engine seconds and names no step. */
  function stepAhead(): AutopilotStep | null {
    if (phase === 'wait') return 'window';
    if (phase !== 'coast') return null;
    return trimIdx < trimJds.length ? 'course-fix' : 'arrival';
  }

  function nextEventSeconds(jd: number): number {
    if (phase === 'wait') return (departStartJd - jd) * SECONDS_PER_DAY;
    const nextTrim = trimIdx < trimJds.length ? trimJds[trimIdx]! : brakeJd;
    return (nextTrim - jd) * SECONDS_PER_DAY;
  }

  /** Where a steered departure points: along the ship's motion about the origin. */
  function departureDirection(jd: number, velocity: FlightVec, out: FlightVec): boolean {
    env.bodyVelocityAt(originId, jd, originVel);
    out.x = velocity.x - originVel.x;
    out.y = velocity.y - originVel.y;
    out.z = velocity.z - originVel.z;
    const l = norm(out);
    if (!(l > 0)) return false;
    out.x /= l;
    out.y /= l;
    out.z /= l;
    return true;
  }

  /** Period of the ship's own orbit about the origin, seconds; NaN off a
   *  closed one. */
  function departurePeriodS(input: AutopilotInput): number {
    return departureOrbitPeriodS(env, originId, input.jd, input.at, input.velocity);
  }

  /**
   * When the departure opens, so that the prograde climb from the ship's own
   * orbit leaves along the plan's asymptote as near as that orbit's plane
   * allows. The climb is the one `finiteEscapeEnd` integrates, from where the
   * ship coasts to by each trial start; its exit turns with the start, so each
   * round moves the start by the angle still missing over the rate it turns
   * at. A reason where the climb alone does not fit under the departure's
   * ceiling at the timed start; a swing after it that does not fit is left to
   * the course fixes. Where no start can be timed near the schedule's, the
   * schedule's own, as an untimed departure flies.
   */
  function timeDeparture(input: AutopilotInput, periodS: number, leadS: number): number | string {
    return timedDepartureStartJd(plan, env, accel, input, periodS, leadS, departStartJd);
  }

  /** What a steered departure still owes, from the energy it has to reach. */
  function departureDvToGo(jd: number, at: FlightVec, velocity: FlightVec, target: number): number {
    const gm = env.gmOf(originId);
    env.bodyAt(originId, jd, originAt);
    env.bodyVelocityAt(originId, jd, originVel);
    rel.x = at.x - originAt.x;
    rel.y = at.y - originAt.y;
    rel.z = at.z - originAt.z;
    relVel.x = velocity.x - originVel.x;
    relVel.y = velocity.y - originVel.y;
    relVel.z = velocity.z - originVel.z;
    const r = norm(rel);
    const v = norm(relVel);
    if (!(r > 0) || !(v > 0)) return 0;
    const energy = (v * v) / 2 - gm / r;
    const want = (v * v) + 2 * (target - energy);
    if (!(want > 0)) return 0;
    return Math.sqrt(want) - v;
  }

  /** What a BOUND departure still owes: the gap between the ship's own speed
   *  about the origin and the transfer ellipse's speed at the radius the ship
   *  is at, signed, so a negative gap is a burn that has to point backwards.
   *  NaN where the ellipse does not reach the ship at all. */
  function boundDvToGo(
    jd: number, at: FlightVec, velocity: FlightVec, target: number,
  ): number {
    const gm = env.gmOf(originId);
    env.bodyAt(originId, jd, originAt);
    env.bodyVelocityAt(originId, jd, originVel);
    rel.x = at.x - originAt.x;
    rel.y = at.y - originAt.y;
    rel.z = at.z - originAt.z;
    relVel.x = velocity.x - originVel.x;
    relVel.y = velocity.y - originVel.y;
    relVel.z = velocity.z - originVel.z;
    const r = norm(rel);
    const v = norm(relVel);
    if (!(r > 0) || !(v > 0) || !(gm > 0)) return Number.NaN;
    const wantSq = 2 * target + (2 * gm) / r;
    if (!(wantSq > 0)) return Number.NaN;
    return Math.sqrt(wantSq) - v;
  }

  /** The plane the plan's own departure push leaves the ship in: its burn point
   *  crossed with the velocity the instant push leaves there, turn included. */
  function plannedNormal(): FlightVec | null {
    const lb = departure?.localBurn ?? null;
    if (departure === null || lb === null) return null;
    const dv = departure.impulseDvKmS;
    const vx = lb.velocityKmS.x + lb.thrustDirection.x * dv;
    const vy = lb.velocityKmS.y + lb.thrustDirection.y * dv;
    const vz = lb.velocityKmS.z + lb.thrustDirection.z * dv;
    const p = lb.positionKm;
    const n = { x: p.y * vz - p.z * vy, y: p.z * vx - p.x * vz, z: p.x * vy - p.y * vx };
    const l = norm(n);
    if (!(l > 0)) return null;
    n.x /= l;
    n.y /= l;
    n.z /= l;
    return n;
  }

  // A bound departure that carries the plan's turn, or whose push leans off the
  // ship's motion: the velocity to go is what a Lambert solve from where the
  // ship is now to the plan's arrival point asks for, less the velocity it has,
  // so the burn ends on the conic the plan priced.
  function steerBoundVector(input: AutopilotInput, order: BurnOrder, pole: FlightVec): boolean {
    const gm = env.gmOf(originId);
    // The same point the trims steer to: the periapsis where the capture
    // prices an orbit into the body, the velocity-match standoff otherwise.
    aimPoint(input.jd, input.at, input.velocity, arriveAt);
    env.bodyAt(originId, plan.arrivalEpochJd, originThen);
    arriveAt.x -= originThen.x;
    arriveAt.y -= originThen.y;
    arriveAt.z -= originThen.z;
    env.bodyAt(originId, input.jd, originAt);
    env.bodyVelocityAt(originId, input.jd, originVel);
    rel.x = input.at.x - originAt.x;
    rel.y = input.at.y - originAt.y;
    rel.z = input.at.z - originAt.z;
    relVel.x = input.velocity.x - originVel.x;
    relVel.y = input.velocity.y - originVel.y;
    relVel.z = input.velocity.z - originVel.z;
    const tofS = (plan.arrivalEpochJd - input.jd) * SECONDS_PER_DAY;
    const solution = tofS > 0 && gm > 0 ? lambert(rel, arriveAt, tofS, gm, pole) : null;
    if (solution !== null && solution.ok) {
      const dx = solution.v1.x - relVel.x;
      const dy = solution.v1.y - relVel.y;
      const dz = solution.v1.z - relVel.z;
      const gap = Math.hypot(dx, dy, dz);
      order.dvToGoMps = gap;
      if (gap > 0) {
        order.direction.x = dx / gap;
        order.direction.y = dy / gap;
        order.direction.z = dz / gap;
      }
      heldGoMps = gap;
      heldSpentMps = order.spentMps;
      return true;
    }
    // Inside the solver's singular band the last solve's direction is held and
    // the push it asked for is run down.
    if (!Number.isFinite(heldGoMps)) return false;
    order.dvToGoMps = Math.max(0, heldGoMps - (order.spentMps - heldSpentMps));
    return true;
  }

  // A departure that stays inside the origin's well: the ship's motion about it
  // again, but the stop is the transfer ellipse's own speed at the burn point
  // rather than an escape, and the burn points BACKWARDS where the ellipse the
  // plan wants is slower than the orbit the ship is on. There is no second law
  // after it: a bound transfer has no asymptote to put anywhere.
  function steerBoundDeparture(input: AutopilotInput, order: BurnOrder): boolean {
    if (order.boundNormal !== null) return steerBoundVector(input, order, order.boundNormal);
    const gap = boundDvToGo(input.jd, input.at, input.velocity, order.targetEnergy);
    if (!Number.isFinite(gap)) return false;
    if (!departureDirection(input.jd, input.velocity, order.direction)) return false;
    if (gap < 0) {
      order.direction.x = -order.direction.x;
      order.direction.y = -order.direction.y;
      order.direction.z = -order.direction.z;
    }
    order.dvToGoMps = Math.abs(gap);
    return true;
  }

  // Where a steered departure points: the ENERGY first, along the ship's motion
  // about the origin, then the POINTING onto the plan's own conic once it escapes.
  function steerDeparture(input: AutopilotInput, order: BurnOrder): boolean {
    if (order.bound) return steerBoundDeparture(input, order);
    const conic = order.pointing || config.progradeDeparture !== true
      ? conicDvToGo(input)
      : -1;
    if (!order.pointing) {
      const deficit = departureDvToGo(
        input.jd, input.at, input.velocity, order.targetEnergy);
      // The pointing law waits for the energy: on a closed orbit its target is
      // carried away faster than the drive closes on it and the chase never ends.
      const takeOver = conic >= 0 && deficit <= DEPARTURE_STOP_MPS;
      if (!takeOver) {
        if (!departureDirection(input.jd, input.velocity, order.direction)) return false;
        order.dvToGoMps = deficit;
        return true;
      }
      // Skipped where it wants more than the departure's ceiling has left: the
      // trim ladder buys that asymptote in open space for a fraction of it.
      if (conic > order.ceilingMps - order.spentMps) {
        order.dvToGoMps = 0;
        return true;
      }
      order.pointing = true;
    }
    if (conic < 0) return false;
    order.dvToGoMps = conic;
    if (!(conic > 0)) return true;
    order.direction.x = (required.x - relVel.x) / conic;
    order.direction.y = (required.y - relVel.y) / conic;
    order.direction.z = (required.z - relVel.z) / conic;
    return true;
  }

  /** What the pointing law owes from here, leaving the velocity it wants in
   *  `required` and the ship's own origin-relative one in `relVel`. -1 where
   *  there is no such conic. */
  function conicDvToGo(input: AutopilotInput): number {
    env.bodyAt(originId, input.jd, originAt);
    env.bodyVelocityAt(originId, input.jd, originVel);
    rel.x = input.at.x - originAt.x;
    rel.y = input.at.y - originAt.y;
    rel.z = input.at.z - originAt.z;
    relVel.x = input.velocity.x - originVel.x;
    relVel.y = input.velocity.y - originVel.y;
    relVel.z = input.velocity.z - originVel.z;
    const vInf = departure === null ? 0 : departure.vInfKmS * 1000;
    const ok = departureRequiredVelocity(
      rel, relVel, env.gmOf(originId), vInf, asymptote, required);
    if (!ok) return -1;
    return Math.hypot(
      required.x - relVel.x, required.y - relVel.y, required.z - relVel.z);
  }

  /** Engine-on seconds the burn in hand has left at a throttle. A frame whose
   *  nose is still slewing carries no throttle, and the count it owes the panel
   *  is the one the burn runs at once the engine opens. */
  function burnSecondsLeft(dvToGoMps: number, throttle: number): number {
    const rate = accel * (throttle > 0 ? throttle : 1);
    if (!(rate > 0) || !(dvToGoMps > 0)) return 0;
    return dvToGoMps / rate;
  }

  /** The velocity a ship where `at` is needs to ride the destination's own
   *  orbit about `matchParentId`, turned onto the ship (`coOrbitVelocityFor`):
   *  the same shape, so it does not swing away and back, and the park's period,
   *  so it opens its distance from the moon slowly and never closes it. */
  function coOrbitVelocity(jd: number, at: FlightVec, out: FlightVec): boolean {
    moonState(jd, moonRel, moonRelVel);
    shipRel.x = at.x - originAt.x;
    shipRel.y = at.y - originAt.y;
    shipRel.z = at.z - originAt.z;
    if (!coOrbitVelocityFor(env.gmOf(matchParentId), moonRel, moonRelVel, shipRel,
      config.coOrbitUnrotated === true, out, park?.semiMajorM)) {
      return false;
    }
    out.x += originVel.x;
    out.y += originVel.y;
    out.z += originVel.z;
    return true;
  }

  /**
   * When the capture's throttle opens, timed off the approach the ship is
   * really on rather than the plan's: into orbit, the finite burn's own lead
   * before the osculating periapsis at the excess speed the ship carries; onto
   * a moon's orbit, where the match flown from now on comes to rest level with
   * the park (`matchBrakeStartS`).
   */
  function timeBrake(input: AutopilotInput, law: 'orbit' | 'match'): number {
    if (!(accel > 0)) return brakeJd;
    if (law === 'match') {
      const found = flyMatch(input);
      return found === null ? brakeJd : input.jd + found.startS / SECONDS_PER_DAY;
    }
    const gm = env.gmOf(destinationId);
    aboutDestination(input);
    const toPeriapsisS = periapsisInS(rel, relVel, gm);
    if (Number.isNaN(toPeriapsisS)) return brakeJd;
    const v = norm(relVel);
    const energy = (v * v) / 2 - gm / norm(rel);
    const live = finite === null ? null
      : finiteCaptureOf(gm / 1e9, parkingRadiusM / 1000, driveAt(input) / 1000, Math.sqrt(2 * energy) / 1000, climbs);
    const leadS = live === null ? plannedLeadS : live.capture.leadS;
    return input.jd + (toPeriapsisS - leadS) / SECONDS_PER_DAY;
  }

  /** The ship about the destination into `rel` and `relVel`. */
  function aboutDestination(input: AutopilotInput): void {
    env.bodyAt(destinationId, input.jd, destAt);
    env.bodyVelocityAt(destinationId, input.jd, destVel);
    rel.x = input.at.x - destAt.x;
    rel.y = input.at.y - destAt.y;
    rel.z = input.at.z - destAt.z;
    relVel.x = input.velocity.x - destVel.x;
    relVel.y = input.velocity.y - destVel.y;
    relVel.z = input.velocity.z - destVel.z;
  }

  /** Metres a second squared the drive delivers this frame at full throttle. */
  function driveAt(input: AutopilotInput): number {
    const cap = input.throttleCap;
    return cap === undefined ? accel : accel * Math.min(1, Math.max(0, cap));
  }

  /**
   * The brake's planned opening, and the rungs counted back from it, moved to
   * the drive this frame has. The schedule prices them at full thrust, and a
   * drive held to half opens its brake hours earlier: the rung an hour ahead of
   * the full-thrust brake then falls inside the braking, and the brake waits
   * for it.
   */
  function planForDrive(input: AutopilotInput): void {
    if (captureLaw !== 'orbit' || capture === null) return;
    const drive = driveAt(input);
    if (!(drive > 0) || drive === plannedDriveMps2) return;
    plannedDriveMps2 = drive;
    let leadS = plannedLeadS;
    aimForDriveM = null;
    if (drive !== accel) {
      if (profile !== null) {
        leadS = profileLeadS(capture.vInfKmS * 1000, profile.circularMps, drive);
      } else if (finite !== null) {
        const priced = finiteCaptureOf(env.gmOf(destinationId) / 1e9, parkingRadiusM / 1000,
          drive / 1000, capture.vInfKmS, climbs);
        if (priced !== null) {
          leadS = priced.capture.leadS;
          if (aimsAtFinite) aimForDriveM = priced.capture.periapsisKm * 1000;
        }
      }
    }
    brakeJd = plan.arrivalEpochJd - leadS / SECONDS_PER_DAY;
    for (let i = trimIdx; i < trimJds.length; i++) {
      const s = schedule.trimFromBrakeS[i];
      if (s !== null && s !== undefined) trimJds[i] = brakeJd - s / SECONDS_PER_DAY;
    }
  }

  /** When a profile capture's throttle opens: where the stop at the closest
   *  approach of the ship's own line, on CAPTURE_PROFILE_SHARE of the drive
   *  this frame has, is due. Reads `rel` and `relVel` as `aboutDestination`
   *  left them. */
  function profileOpenJd(input: AutopilotInput, p: ProfileCapture): number {
    const u = norm(relVel);
    if (!(u > 0)) return input.jd;
    const toClosestM = -(rel.x * relVel.x + rel.y * relVel.y + rel.z * relVel.z) / u;
    return input.jd
      + Math.max(0, toClosestM / u - profileLeadS(u, p.circularMps, driveAt(input))) / SECONDS_PER_DAY;
  }

  /**
   * A profile capture's stop, re-read every frame: the braking that brings the
   * ship to the circle's speed exactly where its line passes closest, the push
   * across the line that puts that pass on the parking radius, and the pull
   * cancelled so the line stays a line. False once the stop is all but done or
   * the pass is behind the ship, which is where the circle takes over.
   */
  function steerProfile(input: AutopilotInput, order: BurnOrder, p: ProfileCapture): boolean {
    const drive = driveAt(input);
    const u = norm(relVel);
    const r = norm(rel);
    if (!(drive > 0) || !(u > 0) || !(r > 0)) return false;
    const along = (rel.x * relVel.x + rel.y * relVel.y + rel.z * relVel.z) / u;
    const toClosestM = -along;
    if (toClosestM <= 0 || u - p.circularMps <= drive * PROFILE_TERMINAL_S) return false;
    const ux = relVel.x / u;
    const uy = relVel.y / u;
    const uz = relVel.z / u;
    // The closest approach of the line, and how far it has to move to sit on
    // the parking radius.
    let cx = rel.x - along * ux;
    let cy = rel.y - along * uy;
    let cz = rel.z - along * uz;
    let b = Math.hypot(cx, cy, cz);
    if (!(b > 1e-6 * r)) {
      // Headed at the centre: pass it on any side, a metre across to start.
      cx = uy;
      cy = -ux;
      cz = 0;
      b = Math.hypot(cx, cy, cz);
      if (!(b > 1e-9)) {
        cx = 0;
        cy = uz;
        cz = -uy;
        b = Math.hypot(cx, cy, cz);
      }
      cx /= b;
      cy /= b;
      cz /= b;
      b = 1;
    }
    const k = p.radiusM / b - 1;
    const tgoS = (2 * toClosestM) / (u + p.circularMps);
    if (order.opened !== true && input.jd >= order.startJd) order.opened = true;
    const opened = order.opened === true;
    let brake = (u * u - p.circularMps * p.circularMps) / (2 * toClosestM);
    if (!opened) brake = Math.max(brake, CAPTURE_PROFILE_SHARE * drive);
    // Zero-effort-miss steering on the pass, whose miss is across the motion.
    let lk = (6 * k) / (tgoS * tgoS);
    const lateral = Math.abs(lk) * b;
    const lateralCap = PROFILE_LATERAL_SHARE * drive;
    if (lateral > lateralCap) lk *= lateralCap / lateral;
    const g = p.gm / (r * r * r);
    const ax = -brake * ux + lk * cx + g * rel.x;
    const ay = -brake * uy + lk * cy + g * rel.y;
    const az = -brake * uz + lk * cz + g * rel.z;
    const al = Math.hypot(ax, ay, az);
    if (!(al > 0)) return false;
    order.direction.x = ax / al;
    order.direction.y = ay / al;
    order.direction.z = az / al;
    order.throttleShare = Math.min(1, al / drive);
    order.dvToGoMps = u - p.circularMps;
    if (!opened) {
      order.startJd = profileOpenJd(input, p);
      brakeJd = order.startJd;
    }
    return true;
  }

  /** The circle at the radius the ship stands at, in the plane it is moving
   *  in and the way it is going round: what a capture ends on. Reads `rel`
   *  and `relVel` as `aboutDestination` left them. */
  function steerCircle(order: BurnOrder, gm: number): void {
    const r = norm(rel);
    let hx = rel.y * relVel.z - rel.z * relVel.y;
    let hy = rel.z * relVel.x - rel.x * relVel.z;
    let hz = rel.x * relVel.y - rel.y * relVel.x;
    let hl = Math.hypot(hx, hy, hz);
    if (!(hl > 1e-9 * r * norm(relVel))) {
      // Falling straight in or out: any plane through the radius will do.
      hx = -rel.y;
      hy = rel.x;
      hz = 0;
      hl = Math.hypot(hx, hy, hz);
      if (!(hl > 0)) {
        hx = 0;
        hy = -rel.z;
        hz = rel.y;
        hl = Math.hypot(hx, hy, hz);
      }
    }
    const circle = Math.sqrt(gm / r) / (hl * r);
    const dx = (hy * rel.z - hz * rel.y) * circle - relVel.x;
    const dy = (hz * rel.x - hx * rel.z) * circle - relVel.y;
    const dz = (hx * rel.y - hy * rel.x) * circle - relVel.z;
    const gap = Math.hypot(dx, dy, dz);
    order.dvToGoMps = gap;
    order.throttleShare = 1;
    if (gap > 0) {
      order.direction.x = dx / gap;
      order.direction.y = dy / gap;
      order.direction.z = dz / gap;
    }
  }

  /** The lowest the conic the ship is on about the destination comes to its
   *  centre, metres; NaN off a bound orbit. */
  function lowPointM(input: AutopilotInput): number {
    aboutDestination(input);
    const gm = env.gmOf(destinationId);
    const r = norm(rel);
    const v = norm(relVel);
    const energy = (v * v) / 2 - gm / r;
    if (!(gm > 0) || !(r > 0) || !(energy < 0)) return Number.NaN;
    const h = Math.hypot(
      rel.y * relVel.z - rel.z * relVel.y,
      rel.z * relVel.x - rel.x * relVel.z,
      rel.x * relVel.y - rel.y * relVel.x);
    const e = Math.sqrt(Math.max(0, 1 + (2 * energy * h * h) / (gm * gm)));
    return (-gm / (2 * energy)) * (1 - e);
  }

  /** The match flown from where the ship is, with the throttle opening where
   *  it comes to rest level with the park (`matchBrakeStartS`). */
  function flyMatch(input: AutopilotInput): MatchBrake | null {
    if (park === null) return null;
    env.bodyAt(matchParentId, input.jd, originAt);
    env.bodyVelocityAt(matchParentId, input.jd, originVel);
    shipRel.x = input.at.x - originAt.x;
    shipRel.y = input.at.y - originAt.y;
    shipRel.z = input.at.z - originAt.z;
    shipRelVel.x = input.velocity.x - originVel.x;
    shipRelVel.y = input.velocity.y - originVel.y;
    shipRelVel.z = input.velocity.z - originVel.z;
    const toEpochS = (plan.arrivalEpochJd - input.jd) * SECONDS_PER_DAY;
    return matchBrakeStartS(env.gmOf(matchParentId), moonTrackFrom(input.jd),
      shipRel, shipRelVel, accel, park, config.coOrbitUnrotated === true,
      toEpochS > 0 ? toEpochS : 0, DEPARTURE_STOP_MPS);
  }

  /** The tilt off the retrograde an energy-law capture thrusts on this frame,
   *  radians: 0 until its throttle opens, and 0 wherever the guidance does not
   *  close, which is the retrograde capture it was before. Reads `rel` and
   *  `relVel` as `aboutDestination` left them. */
  function captureTiltAt(input: AutopilotInput, order: BurnOrder, gm: number): number {
    if (config.captureUnguided === true || input.jd < order.startJd) return 0;
    const sinceS = (input.jd - tiltJd) * SECONDS_PER_DAY;
    const held = captureTilt === null ? null
      : { tiltRad: captureTilt.tiltRad + captureTilt.rateRadS * sinceS, rateRadS: captureTilt.rateRadS };
    if (held !== null && captureTilt!.burnS - sinceS < CAPTURE_GUIDE_HOLD_S) return held.tiltRad;
    const solved = guidedCaptureTilt(gm, rel, relVel, driveAt(input) * (order.throttleShare ?? 1),
      order.targetEnergy, held?.tiltRad ?? 0, held?.rateRadS ?? 0, held === null ? 3 : 1);
    if (solved === null) return held?.tiltRad ?? 0;
    captureTilt = solved;
    tiltJd = input.jd;
    return solved.tiltRad;
  }

  // A capture re-read every frame: the direction it thrusts along and what it
  // still owes on its own stopping rule.
  function steerCapture(input: AutopilotInput, order: BurnOrder): boolean {
    if (order.law === 'match') {
      if (!coOrbitVelocity(input.jd, input.at, coOrbit)) return false;
      const dx = coOrbit.x - input.velocity.x;
      const dy = coOrbit.y - input.velocity.y;
      const dz = coOrbit.z - input.velocity.z;
      const gap = Math.hypot(dx, dy, dz);
      order.dvToGoMps = gap;
      if (gap > 0) {
        order.direction.x = dx / gap;
        order.direction.y = dy / gap;
        order.direction.z = dz / gap;
      }
      return true;
    }
    const gm = env.gmOf(destinationId);
    aboutDestination(input);
    const r = norm(rel);
    const v = norm(relVel);
    if (!(gm > 0) || !(r > 0) || !(v > 0)) return false;
    if (order.circularize !== true) {
      if (profile !== null) {
        if (steerProfile(input, order, profile)) return true;
      } else {
        // The speed to shed down to the parking orbit's energy. Short of the circle's
        // own speed with the low point under the orbit, braking on only drops the ship
        // into the body; a weak drive spirals in above it, the priced burn run backwards.
        const excess = (v * v) / 2 - gm / r - order.targetEnergy;
        if (excess <= 0 || v > Math.sqrt(gm / r) || conicPeriapsisM(rel, relVel, gm) >= parkingRadiusM) {
          order.dvToGoMps = excess > 0 ? v - Math.sqrt(Math.max(0, v * v - 2 * excess)) : 0;
          const tilt = excess > 0 ? captureTiltAt(input, order, gm) : 0;
          const c = Math.cos(tilt) / v;
          let s = Math.sin(tilt) / v;
          // Across the motion in its own plane, toward the side it turns.
          let px = 0;
          let py = 0;
          let pz = 0;
          if (s !== 0) {
            const hx = rel.y * relVel.z - rel.z * relVel.y;
            const hy = rel.z * relVel.x - rel.x * relVel.z;
            const hz = rel.x * relVel.y - rel.y * relVel.x;
            const hl = Math.hypot(hx, hy, hz);
            if (hl > 0) {
              px = (hy * relVel.z - hz * relVel.y) / hl;
              py = (hz * relVel.x - hx * relVel.z) / hl;
              pz = (hx * relVel.y - hy * relVel.x) / hl;
            } else {
              s = 0;
            }
          }
          order.direction.x = -c * relVel.x + s * px;
          order.direction.y = -c * relVel.y + s * py;
          order.direction.z = -c * relVel.z + s * pz;
          return true;
        }
      }
      order.circularize = true;
    }
    steerCircle(order, gm);
    return true;
  }

  function flyBurn(input: AutopilotInput): AutopilotCommand {
    const order = burn;
    if (order === null) return idle(rates[0]!, 0);
    if (order.steered) {
      if (!steerDeparture(input, order)) {
        return finish('refused', 'the ship has no course off the body it is leaving');
      }
      if (order.spentMps > order.ceilingMps) {
        return finish('refused', 'the departure burn is costing more than the plan can carry');
      }
    }
    if (order.law !== null) {
      if (!steerCapture(input, order)) {
        return finish('refused', `the ship has no course about ${plan.destinationName}`);
      }
      if (order.spentMps > order.ceilingMps) {
        return finish('refused', 'the capture burn is costing more than the plan can carry');
      }
    }
    const stopMps = order.steered || order.law !== null ? DEPARTURE_STOP_MPS : BURN_STOP_MPS;
    if (order.dvToGoMps <= stopMps) return endBurn(input);
    const stepS = burnStepSeconds(input.at, input.jd);
    const warpRate = warpForSpan(stepS);
    // The ceiling a player's own ask is held to while the engine is open: the
    // caller splits such a frame into steps of `stepS`, so what the burn can
    // hold is what that many of them carry rather than what one does. The
    // autopilot goes on asking for the unsplit rate, which is the pace a burn
    // is worth watching at; the faster one is there for a player who asks.
    const splitRate = config.burnHoldsRate === true
      ? warpRate : warpForSpan(stepS * BURN_STEPS_PER_FRAME);
    burnSpanRate = config.burnHoldsRate === true ? warpRate : Math.max(splitRate, stepS * BURN_STEPS_PER_FRAME * fps);
    if (config.noSteer === true) {
      // The control: the throttle goes along whatever nose the ship carries.
      axes.pitch = 0;
      axes.yaw = 0;
      axes.roll = 0;
      const spend = Math.min(order.dvToGoMps, accel * input.dtSimS);
      order.spentMps += spend;
      flownSpent += spend;
      if (!order.steered && order.law === null) order.dvToGoMps -= spend;
      const throttle = input.dtSimS > 0 ? Math.min(1, spend / (accel * input.dtSimS)) : 0;
      return command(throttle, warpRate, null, 0,
        burnSecondsLeft(order.dvToGoMps, throttle), stepS, splitRate);
    }
    noseTarget.x = order.direction.x;
    noseTarget.y = order.direction.y;
    noseTarget.z = order.direction.z;
    const errorRad = steerAxes(
      input.state, noseTarget, config.turnRateRadS, input.attitudeDtS, axes);
    // A split frame's step turns on simulated seconds, which lands a whole turn
    // in one rendered frame at warp; a turn that wide gets the frame's real ones.
    const slewing = errorRad > SLEW_FRAME_RAD && config.slewInOneFrame !== true;
    const turnS = slewing
      ? Math.min(input.attitudeDtS, input.slewRealS ?? input.attitudeDtS) : input.attitudeDtS;
    if (turnS < input.attitudeDtS) {
      const rate = Math.hypot(axes.pitch, axes.yaw, axes.roll);
      const want = Math.min(errorRad, config.turnRateRadS * turnS)
        / (config.turnRateRadS * input.attitudeDtS);
      const k = rate > 0 ? want / rate : 0;
      axes.pitch *= k;
      axes.yaw *= k;
      axes.roll *= k;
    }
    const reach = Math.max(ALIGN_TOLERANCE_RAD, config.turnRateRadS * turnS);
    if (errorRad > reach) {
      // The ceiling rides the slew while the nose tracks a moving direction: falling to the unsplit rate would collapse the clock whenever the nose came off it.
      const frozen = config.burnFrozenNose === true;
      const slewRate = warpForSlew(input.jd, errorRad, warpRate);
      // A wide turn is paced at its own rate: at the burn's pace the ship coasts on round the orbit it turns on.
      if (slewing) burnSpanRate = Math.min(burnSpanRate, slewRate);
      return command(0, slewRate, noseTarget, errorRad, burnSecondsLeft(order.dvToGoMps, 0),
        frozen ? 0 : stepS, frozen ? warpRate : slewing ? slewRate : splitRate);
    }
    if (input.jd < order.startJd) {
      const toStart = (order.startJd - input.jd) * SECONDS_PER_DAY;
      return command(0, warpForWait(toStart, warpRate), noseTarget, errorRad, toStart);
    }
    // What the drive delivers this frame, which is what the burn is charged.
    const capacity = driveAt(input) * input.dtSimS * (order.throttleShare ?? 1);
    if (!(capacity > 0)) {
      return command(0, warpRate, noseTarget, errorRad,
        burnSecondsLeft(order.dvToGoMps, 0), stepS, splitRate);
    }
    const spend = Math.min(order.dvToGoMps, capacity);
    order.spentMps += spend;
    flownSpent += spend;
    if (!order.steered && order.law === null) order.dvToGoMps -= spend;
    const throttle = spend / (accel * input.dtSimS);
    return command(throttle, warpRate, noseTarget, errorRad,
      burnSecondsLeft(order.dvToGoMps, throttle), stepS, splitRate);
  }

  function endBurn(input: AutopilotInput): AutopilotCommand {
    const order = burn;
    burn = null;
    if (order !== null && order.label === 'trim') {
      trimSpent += order.spentMps;
      trimIdx++;
    }
    if (order !== null && order.label === 'capture') {
      if (order.law === 'match' || capture?.frame !== 'body-relative') {
        return finish('arrived', order.law === 'match'
          ? `the autopilot has matched ${plan.destinationName}'s orbit and left you beside it`
          : `the autopilot is in orbit at ${plan.destinationName}`);
      }
      // An orbit whose low point is under the ground is not one: the circle at
      // the radius the ship stands at is, where that is itself clear of it.
      const low = lowPointM(input);
      if (low > env.radiusOf(destinationId)) {
        return finish('arrived', `the autopilot is in orbit at ${plan.destinationName}`);
      }
      if (order.law === 'orbit' && order.circularize !== true) {
        order.circularize = true;
        burn = order;
        return flyBurn(input);
      }
      return finish('handback', Number.isNaN(low)
        ? `the capture could not put you into orbit at ${plan.destinationName}; you have the stick`
        : `the capture could not leave the ship on an orbit clear of ${plan.destinationName}'s surface; you have the stick`);
    }
    phase = 'coast';
    const next = nextEventSeconds(input.jd);
    return idle(warpForWait(next, warpForBurn(input.at, input.jd)), next);
  }

  function update(input: AutopilotInput): AutopilotCommand {
    callStartMs = performance.now();
    const out = step(input);
    if (climbsWarm === false && phase === 'coast' && solve === null) climbsWarm = warmClimbs(input);
    held = out;
    return out;
  }

  /** Fly the capture's climbs a slice further on a coasting frame, so the first
   *  aim or brake timing that prices them does not fly 120,000 steps at once. */
  function warmClimbs(input: AutopilotInput): boolean {
    if (climbs === undefined || finite === null || capture === null || captureLaw !== 'orbit') return true;
    const gm = env.gmOf(destinationId) / 1e9;
    const vInf = capture.vInfKmS * CLIMB_WARM_VINF_FACTOR;
    const aimDone = warmFiniteCapture(climbs, gm, finite.parkingRadiusKm, accel / 1000, vInf, CLIMB_WARM_STEPS);
    const brakeDone = warmFiniteCapture(climbs, gm, parkingRadiusM / 1000, driveAt(input) / 1000, vInf,
      CLIMB_WARM_STEPS);
    return aimDone && brakeDone;
  }

  function step(input: AutopilotInput): AutopilotCommand {
    if (ended !== null) return idle(rates[0]!, 0);
    if (input.pilotInput) {
      return finish('handback', 'you have the stick');
    }
    if (solve !== null) return advanceSolve(input);
    if (phase === 'wait') {
      const leadS = alignLeadS(input);
      waitTurnLeadS = leadS;
      if (!departTimed) {
        const periodS = departurePeriodS(input);
        const toStartS = (departStartJd - input.jd) * SECONDS_PER_DAY;
        // An unreadable period waits on to the start and is read again there.
        const reachS = Number.isFinite(periodS) ? Math.max(periodS, DEPARTURE_TIMING_REACH_S) : 0;
        waitTimingReachS = reachS;
        // The warp is sized to the burn, not to this point, so a player waiting
        // it out is not slowed for it; a frame past it times the same start.
        if (toStartS - leadS > reachS) {
          return idle(warpForWait(toStartS - leadS, warpForBurn(input.at, input.jd)), toStartS);
        }
        departTimed = true;
        if (Number.isFinite(periodS)) {
          const timed = timeDeparture(input, periodS, leadS);
          if (typeof timed === 'string') return finish('refused', timed);
          departStartJd = timed;
        }
      }
      const toStart = (departStartJd - input.jd) * SECONDS_PER_DAY;
      if (toStart > leadS) {
        return idle(warpForWait(toStart - leadS, warpForBurn(input.at, input.jd)), toStart);
      }
      if (departure === null) {
        return finish('refused', 'the plan carries no departure burn');
      }
      const vInf = departure.vInfKmS * 1000;
      const direction = vec();
      if (!departureDirection(input.jd, input.velocity, direction)) {
        return finish('refused', 'the ship has no motion about the body it is leaving');
      }
      // A moon of the body the ship is orbiting is reached on an ellipse inside
      // that body's well, so the plan carries the ellipse and the burn targets
      // its energy; everything else leaves the well and targets the excess speed
      // the transfer wants outside it.
      const gm = env.gmOf(originId);
      const boundSemiMajorM = config.escapeDeparture === true
        || departure.boundSemiMajorKm === null
        ? 0
        : departure.boundSemiMajorKm * 1000;
      const bound = boundSemiMajorM > 0 && gm > 0;
      const burnOrbitShare = (departure.impulseDvKmS * 1000) / accel / departurePeriodS(input);
      const steerOnConic = bound && config.departureInOwnPlane !== true
        && ((plan.planeChangeEnd === 'departure' && !(burnOrbitShare > TURN_STEER_MAX_ORBIT_SHARE))
          || pushLeansOffMotion(departure.localBurn));
      burn = {
        label: 'departure',
        steered: true,
        direction,
        dvToGoMps: departure.dvKmS * 1000,
        targetEnergy: bound ? -gm / (2 * boundSemiMajorM) : (vInf * vInf) / 2,
        targetVInf: vInf,
        bound,
        boundNormal: steerOnConic ? plannedNormal() : null,
        pointing: false,
        startJd: departStartJd,
        spentMps: 0,
        ceilingMps: departure.impulseDvKmS * 1000 * DEPARTURE_CEILING_FACTOR,
        law: null,
      };
      phase = 'depart';
      return flyBurn(input);
    }
    if (phase === 'depart' || phase === 'trim' || phase === 'capture') return flyBurn(input);
    if (phase === 'coast') {
      // Once no rung is left before it, the brake is timed off the approach the
      // ship is really on, and the window below opens against that.
      // A profile capture is re-timed every frame, so a drive that has lost
      // thrust since opens it earlier.
      if (captureLaw !== null) {
        if (!brakeTimed) planForDrive(input);
        if (!brakeTimed && !(trimIdx < trimJds.length && trimJds[trimIdx]! < brakeJd)) {
          brakeJd = profile !== null ? brakeJd : timeBrake(input, captureLaw);
          brakeTimed = true;
        }
        if (profile !== null && brakeTimed) {
          aboutDestination(input);
          brakeJd = profileOpenJd(input, profile);
        }
      }
      const captureStartJd = brakeJd;
      const alignJd = captureStartJd - alignLeadS(input) / SECONDS_PER_DAY;
      if (input.solveAhead === true && !trimDue(input.jd)) advanceAhead(input);
      if (trimDue(input.jd)) {
        if (trimArmed) {
          trimArmed = false;
          return beginTrim(input);
        }
        trimArmed = true;
        // Found now, so the match it flies and the solve's first slice fall in
        // two updates and not one.
        if (captureLaw === 'match') trimAim(input, aim);
      }
      // The align lead can still be longer than what is left of a short trip:
      // a rung still due before the brake is flown first.
      const trimBeforeCapture = trimIdx < trimJds.length
        && trimJds[trimIdx]! < captureStartJd;
      if (input.jd >= alignJd && !trimBeforeCapture) {
        if (capture === null) {
          return finish('arrived', `the autopilot has reached ${plan.destinationName}`);
        }
        env.bodyVelocityAt(destinationId, input.jd, destVel);
        const direction = vec();
        direction.x = destVel.x - input.velocity.x;
        direction.y = destVel.y - input.velocity.y;
        direction.z = destVel.z - input.velocity.z;
        const l = norm(direction);
        if (!(l > 0)) {
          return finish('arrived', `the autopilot has reached ${plan.destinationName}`);
        }
        direction.x /= l;
        direction.y /= l;
        direction.z /= l;
        const law = captureLaw;
        burn = {
          label: 'capture',
          steered: false,
          direction,
          dvToGoMps: capture.dvKmS * 1000,
          targetEnergy: law === 'orbit' ? -env.gmOf(destinationId) / (2 * parkingRadiusM) : Number.NaN,
          targetVInf: 0,
          bound: false,
          boundNormal: null,
          pointing: false,
          startJd: brakeJd,
          spentMps: 0,
          ceilingMps: capture.dvKmS * 1000 * (law === null ? 1 : CAPTURE_CEILING_FACTOR),
          law,
          throttleShare: 1,
          circularize: false,
          opened: false,
        };
        phase = 'capture';
        return flyBurn(input);
      }
      const next = Math.min(
        trimIdx < trimJds.length ? (trimJds[trimIdx]! - input.jd) * SECONDS_PER_DAY : Infinity,
        (alignJd - input.jd) * SECONDS_PER_DAY,
      );
      return idle(warpForWait(next, warpForBurn(input.at, input.jd)), next);
    }
    return idle(rates[0]!, 0);
  }

  function cancel(reason = 'the autopilot was stood down'): void {
    if (ended !== null) return;
    phase = 'handback';
    burn = null;
    solve = null;
    ended = { phase: 'handback', reason };
  }

  function captureDueJd(): number {
    return capture === null ? Number.NaN : brakeJd;
  }

  return { update, cancel, freezes, captureDueJd, plan, get aheadOffM() { return aheadOffM; } };
}

const _probe: FlightVec = vec();

function dedupe(ids: readonly string[]): readonly string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (id === '' || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/** One required-velocity solve, part way through: `advance` spends a step
 *  budget on it and says whether it wants another call. Only one may be in
 *  flight on a model at a time, since they share its integrator. */
export interface GuidanceSolve {
  /** Spend up to `stepBudget` steps and `msBudget` milliseconds, whichever ends
   *  first, or the whole solve where `stepBudget` is 0 or less. `running` wants
   *  another call on a later frame. */
  advance(stepBudget: number, msBudget: number): 'running' | 'done' | 'failed';
  /** The velocity solved for. Only meaningful once `advance` said `done`. */
  readonly velocity: FlightVec;
  /** A solve seeded ahead of its date: the velocity carried to first order onto
   *  a ship at `at`, `jd` rather than where the solve predicted it. */
  velocityAt?(at: FlightVec, jd: number, out: FlightVec): void;
}

/** How an arrival moves with a start position (`rr`) and velocity (`rv`), row
 *  major, arrival axis by start axis; null until probed. */
interface AheadSensitivity {
  rr: Float64Array | null;
  rv: Float64Array | null;
}

/** What the guidance believes about the world: a few bodies and one integrator. */
export interface GuidanceModel {
  /** Coast a heliocentric state from `jd0` to `jd1` under `modelIds`' pulls; the
   *  steps it took, or 0 where it ran out of them. */
  propagate(
    at: FlightVec, velocity: FlightVec, jd0: number, jd1: number,
    outAt: FlightVec, outVelocity: FlightVec,
  ): number;
  /** The heliocentric velocity at `at`, `jd0` putting the ship on `target` at
   *  `jdTarget`, by shooting on `propagate`. False where it did not converge. */
  requiredVelocity(
    at: FlightVec, velocity: FlightVec, jd0: number, target: FlightVec, jdTarget: number,
    out: FlightVec,
  ): boolean;
  /** The same solve, handed back part way through so a caller on a frame budget
   *  can spend it a few thousand steps at a time. */
  beginRequiredVelocity(
    at: FlightVec, velocity: FlightVec, jd0: number, target: FlightVec, jdTarget: number,
  ): GuidanceSolve;
  /** The same solve from where the state at `jd0` coasts to by `jd1`: that
   *  predicted position goes in `seedAt`, `aimFor` gives the target off the
   *  predicted state, and `guess` (null: the predicted velocity) starts the search. */
  beginRequiredVelocityAhead(
    at: FlightVec, velocity: FlightVec, jd0: number, jd1: number,
    aimFor: (at: FlightVec, velocity: FlightVec, out: FlightVec) => void, jdTarget: number,
    seedAt: FlightVec, guess: FlightVec | null,
  ): GuidanceSolve;
  /** Steps the last `requiredVelocity` spent, for a cost readout. */
  readonly steps: number;
  /** Metres the last solve left between its own prediction and the target. */
  readonly lastMissM: number;
}

// The guidance's own propagator: the flight's own `stepFlight` over a reduced
// world of Sun, origin and destination, so no trim is solved against a different one.
export function createModel(env: AutopilotEnv, ids: readonly string[]): GuidanceModel {
  const live = ids.filter((id) => env.gmOf(id) > 0);
  const pulls: FlightPull[] = live.map((id) => ({ gm: env.gmOf(id), at: vec() }));
  const field: FlightField = { at: vec(), pulls, frameAccel: null };
  const state: FlightState = {
    orientation: { x: 0, y: 0, z: 0, w: 1 },
    assistAim: null,
    velocity: vec(),
    deltaVSpent: 0,
    assistCatchUp: false,
    grounded: null,
    groundOffset: vec(),
    contactSpeedMps: 0,
    assistCapped: false,
    assistPush: vec(),
  };
  const config: FlightConfig = {
    turnRate: 0,
    accel: 0,
    drag: 0,
    maxSubstepS: MODEL_STEP_MAX_S,
    maxSubsteps: 1,
    assistFree: false,
    assistHeldOnly: false,
    assistFrozenAim: false,
    assistNoFloor: false,
  };
  const delta = vec();
  const end = vec();
  const endVel = vec();
  const residual = vec();
  const probe = vec();
  const trial = vec();
  const jac = new Float64Array(9);
  const best = vec();
  const hold = vec();
  const delta2 = vec();
  let steps = 0;
  let lastMissM = Number.NaN;
  /** Steps the job in flight may still take before it yields the frame. */
  let stepsLeft = Infinity;
  /** performance.now() the slice in flight must yield at, and the countdown to
   *  the next read of it. */
  let sliceDeadlineMs = Infinity;
  let clockCheckLeft = SLICE_CLOCK_STEPS;

  function sliceExpired(): boolean {
    if (sliceDeadlineMs === Infinity) return false;
    if (--clockCheckLeft > 0) return false;
    clockCheckLeft = SLICE_CLOCK_STEPS;
    return performance.now() >= sliceDeadlineMs;
  }

  /** Run a job to the end with no budget, for the callers that are not on a
   *  frame: the known-answer checks and anything off the tick. */
  function drive<T>(job: Generator<void, T, void>): T {
    stepsLeft = Infinity;
    sliceDeadlineMs = Infinity;
    let r = job.next();
    while (!r.done) r = job.next();
    return r.value;
  }

  function stepFor(spanS: number): number {
    let shortest = Math.min(MODEL_STEP_MAX_S, spanS / MODEL_STEPS_PER_SPAN);
    for (let i = 0; i < pulls.length; i++) {
      const p = pulls[i]!;
      const dx = p.at.x - field.at.x;
      const dy = p.at.y - field.at.y;
      const dz = p.at.z - field.at.z;
      const r = Math.hypot(dx, dy, dz);
      if (!(r > 0)) continue;
      const scale = MODEL_STEP_FRACTION * Math.sqrt((r * r * r) / p.gm);
      if (scale < shortest) shortest = scale;
    }
    return Math.max(MODEL_STEP_MIN_S, shortest);
  }

  // The propagation as a resumeable job: it yields on step boundaries and picks up
  // with its own integrator state standing, so nothing else may run on it meanwhile.
  function* propagateJob(
    at: FlightVec, velocity: FlightVec, jd0: number, jd1: number,
    outAt: FlightVec, outVelocity: FlightVec,
  ): Generator<void, number, void> {
    field.at.x = at.x;
    field.at.y = at.y;
    field.at.z = at.z;
    state.velocity.x = velocity.x;
    state.velocity.y = velocity.y;
    state.velocity.z = velocity.z;
    let taken = 0;
    const spanS = (jd1 - jd0) * SECONDS_PER_DAY;
    // Seconds flown, with the date read off them: a date summed a step at a
    // time rounds the same way every step, and 2,000 steps put the model tens
    // of milliseconds off its own clock, a kilometre at a planet's speed.
    let flownS = 0;
    while (flownS < spanS) {
      const leftS = spanS - flownS;
      const jd = jd0 + flownS / SECONDS_PER_DAY;
      for (let i = 0; i < pulls.length; i++) env.bodyAt(live[i]!, jd, pulls[i]!.at);
      const h = Math.min(stepFor(spanS), leftS);
      if (!(h > 0)) break;
      // The kick lands at the step's own midpoint, so that is where the centres
      // are read: the same trade `src/sim/flightCentres.ts` makes per span.
      const mid = jd0 + (flownS + h / 2) / SECONDS_PER_DAY;
      for (let i = 0; i < pulls.length; i++) env.bodyAt(live[i]!, mid, pulls[i]!.at);
      config.maxSubstepS = h;
      stepFlight(state, ZERO_AXES, 0, config, h, 0, delta, field, 0);
      field.at.x += delta.x;
      field.at.y += delta.y;
      field.at.z += delta.z;
      flownS += h;
      taken++;
      if (taken > MODEL_STEP_CEILING) return 0;
      if (--stepsLeft <= 0 || sliceExpired()) yield;
    }
    outAt.x = field.at.x;
    outAt.y = field.at.y;
    outAt.z = field.at.z;
    outVelocity.x = state.velocity.x;
    outVelocity.y = state.velocity.y;
    outVelocity.z = state.velocity.z;
    return taken;
  }

  function propagate(
    at: FlightVec, velocity: FlightVec, jd0: number, jd1: number,
    outAt: FlightVec, outVelocity: FlightVec,
  ): number {
    return drive(propagateJob(at, velocity, jd0, jd1, outAt, outVelocity));
  }

  // Shooting with a capped step, a floored normal matrix, a backtracking line
  // search and the best velocity kept: a trim that halves the miss is still a trim.
  function* requiredVelocityJob(
    at: FlightVec, velocity: FlightVec, jd0: number, target: FlightVec, jdTarget: number,
    out: FlightVec,
  ): Generator<void, boolean, void> {
    steps = 0;
    out.x = velocity.x;
    out.y = velocity.y;
    out.z = velocity.z;
    if (!(jdTarget > jd0)) return false;
    let miss = yield* missOfJob(at, out, jd0, target, jdTarget);
    if (!Number.isFinite(miss)) return false;
    const started = miss;
    best.x = out.x;
    best.y = out.y;
    best.z = out.z;
    let bestMiss = miss;
    for (let iter = 0; iter < SOLVE_MAX_ITERATIONS && bestMiss > SOLVE_TOLERANCE_M; iter++) {
      // `end` still holds the arrival `missOf` just propagated to.
      hold.x = end.x;
      hold.y = end.y;
      hold.z = end.z;
      for (let c = 0; c < 3; c++) {
        trial.x = out.x;
        trial.y = out.y;
        trial.z = out.z;
        if (c === 0) trial.x += SOLVE_PROBE_MPS;
        else if (c === 1) trial.y += SOLVE_PROBE_MPS;
        else trial.z += SOLVE_PROBE_MPS;
        const t = yield* propagateJob(at, trial, jd0, jdTarget, probe, endVel);
        steps += t;
        if (t === 0) break;
        jac[c] = (probe.x - hold.x) / SOLVE_PROBE_MPS;
        jac[3 + c] = (probe.y - hold.y) / SOLVE_PROBE_MPS;
        jac[6 + c] = (probe.z - hold.z) / SOLVE_PROBE_MPS;
      }
      residual.x = hold.x - target.x;
      residual.y = hold.y - target.y;
      residual.z = hold.z - target.z;
      if (!solve3(jac, residual, delta2)) break;
      const stepLen = norm(delta2);
      if (!Number.isFinite(stepLen) || stepLen === 0) break;
      let k = stepLen > SOLVE_STEP_CEILING_MPS ? SOLVE_STEP_CEILING_MPS / stepLen : 1;
      let took = false;
      for (let back = 0; back < SOLVE_BACKTRACKS; back++) {
        trial.x = out.x - delta2.x * k;
        trial.y = out.y - delta2.y * k;
        trial.z = out.z - delta2.z * k;
        const m = yield* missOfJob(at, trial, jd0, target, jdTarget);
        if (Number.isFinite(m) && m < miss) {
          out.x = trial.x;
          out.y = trial.y;
          out.z = trial.z;
          miss = m;
          took = true;
          break;
        }
        k *= 0.5;
      }
      if (!took) break;
      if (miss < bestMiss) {
        bestMiss = miss;
        best.x = out.x;
        best.y = out.y;
        best.z = out.z;
      }
    }
    out.x = best.x;
    out.y = best.y;
    out.z = best.z;
    lastMissM = bestMiss;
    // A solve that could not improve on what it was handed is not a refusal: the
    // deadband reads zero and the next rung tries from a better geometry.
    void started;
    return Number.isFinite(bestMiss);
  }

  /** Propagate and return the arrival miss, leaving the arrival in `end`. */
  function* missOfJob(
    at: FlightVec, velocity: FlightVec, jd0: number, target: FlightVec, jdTarget: number,
  ): Generator<void, number, void> {
    const took = yield* propagateJob(at, velocity, jd0, jdTarget, end, endVel);
    steps += took;
    if (took === 0) return Number.NaN;
    return Math.hypot(end.x - target.x, end.y - target.y, end.z - target.z);
  }

  function requiredVelocity(
    at: FlightVec, velocity: FlightVec, jd0: number, target: FlightVec, jdTarget: number,
    out: FlightVec,
  ): boolean {
    return drive(requiredVelocityJob(at, velocity, jd0, target, jdTarget, out));
  }

  function beginRequiredVelocity(
    at: FlightVec, velocity: FlightVec, jd0: number, target: FlightVec, jdTarget: number,
  ): GuidanceSolve {
    const answer = vec();
    // The state is copied, not held: a solve that spans frames outlives the live
    // objects the tick writes, and holding them converges on nothing in particular.
    const from = { x: at.x, y: at.y, z: at.z };
    const had = { x: velocity.x, y: velocity.y, z: velocity.z };
    const to = { x: target.x, y: target.y, z: target.z };
    return sliced(requiredVelocityJob(from, had, jd0, to, jdTarget, answer), answer);
  }

  function* aheadJob(
    at: FlightVec, velocity: FlightVec, jd0: number, jd1: number,
    aimFor: (at: FlightVec, velocity: FlightVec, out: FlightVec) => void, jdTarget: number,
    seedAt: FlightVec, guess: FlightVec | null, out: FlightVec, sens: AheadSensitivity,
  ): Generator<void, boolean, void> {
    const seedVel = vec();
    const to = vec();
    if (!(jd1 > jd0) || (yield* propagateJob(at, velocity, jd0, jd1, seedAt, seedVel)) === 0) return false;
    aimFor(seedAt, seedVel, to);
    if (!(yield* requiredVelocityJob(seedAt, guess ?? seedVel, jd1, to, jdTarget, out))) return false;
    // How the arrival moves with the start's position and its velocity, probed
    // about the answer, so `velocityAt` can carry it onto the ship really there.
    const base = vec();
    const sink = vec();
    const at1 = vec();
    const vel1 = vec();
    const end1 = vec();
    if ((yield* propagateJob(seedAt, out, jd1, jdTarget, base, sink)) === 0) return true;
    const rr = new Float64Array(9);
    const rv = new Float64Array(9);
    for (let c = 0; c < 6; c++) {
      const k = c < 3 ? AHEAD_PROBE_M : SOLVE_PROBE_MPS;
      at1.x = seedAt.x + (c === 0 ? k : 0);
      at1.y = seedAt.y + (c === 1 ? k : 0);
      at1.z = seedAt.z + (c === 2 ? k : 0);
      vel1.x = out.x + (c === 3 ? k : 0);
      vel1.y = out.y + (c === 4 ? k : 0);
      vel1.z = out.z + (c === 5 ? k : 0);
      if ((yield* propagateJob(at1, vel1, jd1, jdTarget, end1, sink)) === 0) return true;
      const m = c < 3 ? rr : rv;
      m[c % 3] = (end1.x - base.x) / k;
      m[3 + (c % 3)] = (end1.y - base.y) / k;
      m[6 + (c % 3)] = (end1.z - base.z) / k;
    }
    sens.rr = rr;
    sens.rv = rv;
    return true;
  }

  function beginRequiredVelocityAhead(
    at: FlightVec, velocity: FlightVec, jd0: number, jd1: number,
    aimFor: (at: FlightVec, velocity: FlightVec, out: FlightVec) => void, jdTarget: number,
    seedAt: FlightVec, guess: FlightVec | null,
  ): GuidanceSolve {
    const answer = vec();
    const from = { x: at.x, y: at.y, z: at.z };
    const had = { x: velocity.x, y: velocity.y, z: velocity.z };
    const start = guess === null ? null : { x: guess.x, y: guess.y, z: guess.z };
    const sens: AheadSensitivity = { rr: null, rv: null };
    const solve = sliced(aheadJob(from, had, jd0, jd1, aimFor, jdTarget, seedAt, start, answer, sens), answer);
    const miss = vec();
    const fix = vec();
    return {
      velocity: answer,
      advance: solve.advance,
      velocityAt(at: FlightVec, jd: number, out: FlightVec): void {
        out.x = answer.x;
        out.y = answer.y;
        out.z = answer.z;
        if (sens.rr === null || sens.rv === null) return;
        // Where the ship is against where the answer would have it by `jd`.
        const dtS = (jd - jd1) * SECONDS_PER_DAY;
        const dx = at.x - seedAt.x - answer.x * dtS;
        const dy = at.y - seedAt.y - answer.y * dtS;
        const dz = at.z - seedAt.z - answer.z * dtS;
        const r = sens.rr;
        miss.x = r[0]! * dx + r[1]! * dy + r[2]! * dz;
        miss.y = r[3]! * dx + r[4]! * dy + r[5]! * dz;
        miss.z = r[6]! * dx + r[7]! * dy + r[8]! * dz;
        if (!solve3(sens.rv, miss, fix)) return;
        out.x -= fix.x;
        out.y -= fix.y;
        out.z -= fix.z;
      },
    };
  }

  function sliced(job: Generator<void, boolean, void>, answer: FlightVec): GuidanceSolve {
    let settled: 'done' | 'failed' | null = null;
    return {
      velocity: answer,
      advance(stepBudget: number, msBudget: number): 'running' | 'done' | 'failed' {
        if (settled !== null) return settled;
        stepsLeft = stepBudget > 0 ? stepBudget : Infinity;
        clockCheckLeft = SLICE_CLOCK_STEPS;
        sliceDeadlineMs = stepBudget > 0 && msBudget > 0
          ? performance.now() + msBudget : Infinity;
        const r = job.next();
        if (!r.done) return 'running';
        settled = r.value ? 'done' : 'failed';
        return settled;
      },
    };
  }

  return {
    propagate,
    requiredVelocity,
    beginRequiredVelocity,
    beginRequiredVelocityAhead,
    get steps() { return steps; },
    get lastMissM() { return lastMissM; },
  };
}

// `J x = f`, 3x3, by Gaussian elimination with partial pivoting and a Tikhonov
// floor: at half a revolution the out-of-plane column goes to zero without it.
function solve3(j: Float64Array, f: FlightVec, out: FlightVec): boolean {
  let scale = 0;
  for (let i = 0; i < 9; i++) scale = Math.max(scale, Math.abs(j[i]!));
  if (!(scale > 0)) return false;
  const lambda = scale * 1e-6;
  const m = new Float64Array(12);
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) m[r * 4 + c] = j[r * 3 + c]! + (r === c ? lambda : 0);
  }
  m[3] = f.x;
  m[7] = f.y;
  m[11] = f.z;
  for (let col = 0; col < 3; col++) {
    let pivot = col;
    for (let r = col + 1; r < 3; r++) {
      if (Math.abs(m[r * 4 + col]!) > Math.abs(m[pivot * 4 + col]!)) pivot = r;
    }
    if (Math.abs(m[pivot * 4 + col]!) < scale * 1e-14) return false;
    if (pivot !== col) {
      for (let c = 0; c < 4; c++) {
        const t = m[col * 4 + c]!;
        m[col * 4 + c] = m[pivot * 4 + c]!;
        m[pivot * 4 + c] = t;
      }
    }
    for (let r = 0; r < 3; r++) {
      if (r === col) continue;
      const factor = m[r * 4 + col]! / m[col * 4 + col]!;
      if (factor === 0) continue;
      for (let c = col; c < 4; c++) m[r * 4 + c] = m[r * 4 + c]! - factor * m[col * 4 + c]!;
    }
  }
  out.x = m[3]! / m[0]!;
  out.y = m[7]! / m[5]!;
  out.z = m[11]! / m[10]!;
  return Number.isFinite(out.x) && Number.isFinite(out.y) && Number.isFinite(out.z);
}
